/**
 * Конфигурация стилей слоев
 * Используется для:
 * - Рендеринга слоев на карте
 * - Автоматической генерации легенд с правильными цветами и размерами
 */

import { defineMessage } from '@lingui/macro';
import { functionalAreasOperatorTypesWithWhoosh } from './variables';
import { LEGEND_ICON_TYPES } from '../types/legendTypes';

const operatorTypeColor = operator => (
    functionalAreasOperatorTypesWithWhoosh.find(({ value }) => value === operator)?.fill || '#9e9e9e'
);

/**
 * Стиль слоя "Жилые дома"
 * Содержит интерполяции для цветов (по году) и размеров (по населению)
 */
export const HOUSES_STYLE = {
    // Градиент по году постройки (commission_year)
    yearGradient: {
        property: 'commission_year',
        minValue: 1900,
        maxValue: 2010,
        items: [
            { id: 'year-1', label: defineMessage({ message: '< 1900' }), color: '#f7ff00' },
            { id: 'year-2', label: defineMessage({ message:   '1930' }), color: '#d4a537' },
            { id: 'year-3', label: defineMessage({ message:   '1970' }), color: '#c95d8b' },
            { id: 'year-4', label: defineMessage({ message: '> 2010' }), color: '#db36a4' },
        ],
    },
    // Градуировка по населению (total_ppl)
    populationScale: {
        property: 'total_ppl',
        items: [
            { id: 'pop-100', value: 100, label: defineMessage({ message: '<100' }), radius: 2 },
            { id: 'pop-500', value: 500, label: defineMessage({ message: '~500' }), radius: 5 },
            { id: 'pop-800', value: 800, label: defineMessage({ message: '~800' }), radius: 6 },
            { id: 'pop-2000', value: 2000, label: defineMessage({ message: '>2000' }), radius: 11 },
        ],
    },
};

/**
 * MapBox paint стиль для слоя "Жилые дома"
 * Используется для рендеринга на карте
 */
export const HOUSES_MAPBOX_PAINT = {
    'circle-color': [
        'case',
        [ '!', [ 'has', HOUSES_STYLE.yearGradient.property ] ],
        '#ffeb3b',
        [
            'interpolate',
            [ 'linear' ],
            [ 'get', HOUSES_STYLE.yearGradient.property ],
            1900,
            HOUSES_STYLE.yearGradient.items[0].color,
            1930,
            HOUSES_STYLE.yearGradient.items[1].color,
            1970,
            HOUSES_STYLE.yearGradient.items[2].color,
            2010,
            HOUSES_STYLE.yearGradient.items[3].color,
        ],
    ],
    'circle-opacity': 0.5,
    'circle-radius': [
        'interpolate',
        [ 'linear' ],
        [ 'get', HOUSES_STYLE.populationScale.property ],
        HOUSES_STYLE.populationScale.items[0].value,
        HOUSES_STYLE.populationScale.items[0].radius,
        HOUSES_STYLE.populationScale.items[1].value,
        HOUSES_STYLE.populationScale.items[1].radius,
        HOUSES_STYLE.populationScale.items[2].value,
        HOUSES_STYLE.populationScale.items[2].radius,
        HOUSES_STYLE.populationScale.items[3].value,
        HOUSES_STYLE.populationScale.items[3].radius,
    ],
};

/**
 * Стили для градиентных легенд (Scooters, Parkings, Speed, etc.)
 * Используются для GradientLegend компонентов в FilterSidebar.
 *
 * Структура: LAYER_NAME_STYLE = { propertyName: [{ id, label, color }] }
 * - propertyName: имя свойства для использования в компоненте
 * - id: уникальный идентификатор элемента (для React keys)
 * - label: отображаемый текст (используйте defineMessage для i18n)
 * - color: hex цвет для градиента
 *
 * Используется в компонентах:
 * <GradientLegend items={LAYER_NAME_STYLE.propertyName} renderConfig={config} />
 */

// Дефицит/Профицит самокатов
// Компонент: src/components/FilterSidebar/CurDeficitScooters/
export const SCOOTER_DEFICIT_STYLE = {
    deficit: [
        { id: 'deficit-min', label: defineMessage({ message: 'Меньше' }), color: '#9CECFB' },
        { id: 'deficit-max', label: defineMessage({ message: 'Больше' }), color: '#f01625' },
    ],
};

// Компонент: src/components/FilterSidebar/CurProficitScooters/
export const SCOOTER_PROFICIT_STYLE = {
    proficit: [
        { id: 'proficit-min', label: defineMessage({ message: 'Меньше' }), color: '#9CECFB' },
        { id: 'proficit-max', label: defineMessage({ message: 'Больше' }), color: '#f6515c' },
    ],
};

// Баланс парковок (Rebalance)
// Компонент: src/components/FilterSidebar/OperationAreasV2Filter/
export const REBALANCE_PARKING_STYLE = {
    balance: [
        { id: 'rebal-deficit', label: defineMessage({ message: 'Дефицит' }), color: '#0052D4' },
        { id: 'rebal-zero', label: defineMessage({ message: '0' }), color: '#65C7F7' },
        { id: 'rebal-mid', label: defineMessage({ message: '' }), color: '#fefcfc' },
        { id: 'rebal-proficit', label: defineMessage({ message: 'Избыток' }), color: '#f01625' },
    ],
};

// Плотность парковок
export const PARKING_DENSITY_STYLE = {
    density: [
        { id: 'density-1', label: defineMessage({ message: '< 10' }), color: '#440154' },
        { id: 'density-2', label: defineMessage({ message: '20' }),   color: '#414487' },
        { id: 'density-3', label: defineMessage({ message: '30' }),   color: '#2a788e' },
        { id: 'density-4', label: defineMessage({ message: '40' }),   color: '#22a884' },
        { id: 'density-5', label: defineMessage({ message: '50' }),   color: '#7ad151' },
        { id: 'density-6', label: defineMessage({ message: '60' }),   color: '#fde725' },
        { id: 'density-7', label: defineMessage({ message: '70' }),   color: '#fde725' },
        { id: 'density-8', label: defineMessage({ message: '> 80' }), color: '#fde725' },
    ],
};

// Сглаженные показатели аренды
export const SMOOTHED_RENT_STYLE = {
    rent: [
        { id: 'rent-1', label: defineMessage({ message: '1' }),   color: '#0b0405' },
        { id: 'rent-2', label: defineMessage({ message: '2' }),   color: '#28192f' },
        { id: 'rent-3', label: defineMessage({ message: '3' }),   color: '#3b2f5f' },
        { id: 'rent-4', label: defineMessage({ message: '4' }),   color: '#3f498e' },
        { id: 'rent-5', label: defineMessage({ message: '5' }),   color: '#366b9f' },
        { id: 'rent-6', label: defineMessage({ message: '6' }),   color: '#348ba6' },
        { id: 'rent-7', label: defineMessage({ message: '7' }),   color: '#38abac' },
        { id: 'rent-8', label: defineMessage({ message: '8' }),   color: '#55caad' },
        { id: 'rent-9', label: defineMessage({ message: '9' }),   color: '#a3e0ba' },
        { id: 'rent-10', label: defineMessage({ message: '10' }), color: '#def5e5' },
    ],
};

// Неровность дороги
export const ROAD_ROUGHNESS_STYLE = {
    roughness: {
        items: [
            { id: 'roughness-1', value: 'roughness-1', label: defineMessage({ message: '< 0.1' }), color: '#3b21a6' },
            { id: 'roughness-2', value: 'roughness-2', label: defineMessage({ message: '0.2' }),   color: '#5a2d7f' },
            { id: 'roughness-3', value: 'roughness-3', label: defineMessage({ message: '0.3' }),   color: '#8c3a82' },
            { id: 'roughness-4', value: 'roughness-4', label: defineMessage({ message: '0.4' }),   color: '#b84983' },
            { id: 'roughness-5', value: 'roughness-5', label: defineMessage({ message: '0.5' }),   color: '#be3785' },
            { id: 'roughness-6', value: 'roughness-6', label: defineMessage({ message: '0.6' }),   color: '#d85d7c' },
            { id: 'roughness-7', value: 'roughness-7', label: defineMessage({ message: '> 0.7' }), color: '#f0f921' },
        ],
    },
};

// Бронирование самокатов на парковке
export const BOOKING_PARKING_STYLE = {
    booking: [
        { id: 'booking-1', label: defineMessage({ message: '0%' }),   color: '#0d0887' },
        { id: 'booking-2', label: defineMessage({ message: '20%' }),  color: '#792282' },
        { id: 'booking-3', label: defineMessage({ message: '40%' }),  color: '#c23b75' },
        { id: 'booking-4', label: defineMessage({ message: '60%' }),  color: '#f4695c' },
        { id: 'booking-5', label: defineMessage({ message: '80%' }),  color: '#feb47b' },
        { id: 'booking-6', label: defineMessage({ message: '100%' }), color: '#fcfdbf' },
    ],
};

// Бронирование по датам
export const PARKINGS_BOOKINGS_DATE_STYLE = {
    bookingsDate: [
        { id: 'booking-few', label: defineMessage({ message: 'Мало' }),   color: '#440154' },
        { id: 'booking-2', label: defineMessage({ message: '' }),         color: '#414487' },
        { id: 'booking-3', label: defineMessage({ message: '' }),         color: '#2a788e' },
        { id: 'booking-4', label: defineMessage({ message: '' }),         color: '#22a884' },
        { id: 'booking-5', label: defineMessage({ message: '' }),         color: '#7ad151' },
        { id: 'booking-many', label: defineMessage({ message: 'Много' }), color: '#fde725' },
    ],
};

// Агрегированные треки (август)
export const AGGREGATED_TRACKS_AUGUST_STYLE = {
    tracks: [
        { id: 'tracks-1', label: defineMessage({ message: '<0.3' }), color: '#0d0887' },
        { id: 'tracks-2', label: defineMessage({ message: '1' }),    color: '#6b00a8' },
        { id: 'tracks-3', label: defineMessage({ message: '4' }),    color: '#e16461' },
        { id: 'tracks-4', label: defineMessage({ message: '8' }),    color: '#fca736' },
        { id: 'tracks-5', label: defineMessage({ message: '>25' }),  color: '#f0f921' },
    ],
};


// Агрегированные поездки (август) - H3 с динамическим градиентом
export const AGGREGATED_TRIPS_AUGUST_H3_STYLE = {
    trips: [
        { id: 'trips-1', label: defineMessage({ message: '<0.3' }), color: '#0d0887' },
        { id: 'trips-2', label: defineMessage({ message: '1' }),    color: '#6b00a8' },
        { id: 'trips-3', label: defineMessage({ message: '4' }),    color: '#e16461' },
        { id: 'trips-4', label: defineMessage({ message: '8' }),    color: '#fca736' },
        { id: 'trips-5', label: defineMessage({ message: '>25' }),  color: '#f0f921' },
    ],
};

// Агрегированные старты (август)
export const AGGREGATED_STARTS_AUGUST_STYLE = {
    starts: {
        items: [
            { id: 'starts-1', value: 'starts-1', label: defineMessage({ message: '<10' }),  color: '#0d0887' },
            { id: 'starts-2', value: 'starts-2', label: defineMessage({ message: '100' }),  color: '#6b00a8' },
            { id: 'starts-3', value: 'starts-3', label: defineMessage({ message: '300' }),  color: '#e16461' },
            { id: 'starts-4', value: 'starts-4', label: defineMessage({ message: '700' }),  color: '#fca736' },
            { id: 'starts-5', value: 'starts-5', label: defineMessage({ message: '>900' }), color: '#f0f921' },
        ],
    },
};

// Агрегированные финиши (август)
export const AGGREGATED_FINISHES_AUGUST_STYLE = {
    finishes: {
        items: [
            { id: 'finishes-1', value: 'finishes-1', label: defineMessage({ message: '<10' }),  color: '#0d0887' },
            { id: 'finishes-2', value: 'finishes-2', label: defineMessage({ message: '100' }),  color: '#6b00a8' },
            { id: 'finishes-3', value: 'finishes-3', label: defineMessage({ message: '300' }),  color: '#e16461' },
            { id: 'finishes-4', value: 'finishes-4', label: defineMessage({ message: '700' }),  color: '#fca736' },
            { id: 'finishes-5', value: 'finishes-5', label: defineMessage({ message: '>900' }), color: '#f0f921' },
        ],
    },
};

// Кол-во переходов в Change Battery H3
export const RECHARGING_STATS_STYLE = {
    recharges: [
        { id: 'recharges-1', value: 1,   label: defineMessage({ message: '1' }),   color: '#440154' },
        { id: 'recharges-2', value: 5,   label: defineMessage({ message: '5' }),   color: '#414487' },
        { id: 'recharges-3', value: 14,  label: defineMessage({ message: '14' }),  color: '#2a788e' },
        { id: 'recharges-4', value: 42,  label: defineMessage({ message: '42' }),  color: '#22a884' },
        { id: 'recharges-5', value: 100, label: defineMessage({ message: '100' }), color: '#7ad151' },
        { id: 'recharges-6', value: 180, label: defineMessage({ message: '180' }), color: '#c7e020' },
        { id: 'recharges-7', value: 250, label: defineMessage({ message: '250' }), color: '#fde725' },
    ],
};

// Медианная скорость
export const MEDIAN_SPEED_STYLE = {
    speed: {
        items: [
            { id: 'speed-1', label: defineMessage({ message: '10' }), color: '#02ca02' },
            { id: 'speed-2', label: defineMessage({ message: '13' }), color: '#66bd6f' },
            { id: 'speed-3', label: defineMessage({ message: '15' }), color: '#c8fde7' },
            { id: 'speed-4', label: defineMessage({ message: '20' }), color: '#767676' },
            { id: 'speed-5', label: defineMessage({ message: '23' }), color: '#404040' },
        ],
    },
};

// Dynamic Tariff Score
export const DYNAMIC_TARIFF_STYLE = {
    score: {
        items: [
            { id: 'tariff-1', label: '1',   color: 'hsl(242, 89%, 20%)' },
            { id: 'tariff-2', label: '2',   color: '#46039f' },
            { id: 'tariff-3', label: '3',   color: '#5302a3' },
            { id: 'tariff-4', label: '4',   color: '#5f04a8' },
            { id: 'tariff-5', label: '5',   color: '#7300a8' },
            { id: 'tariff-6', label: '6',   color: '#8b0aa5' },
            { id: 'tariff-7', label: '7',   color: '#9c179e' },
            { id: 'tariff-8', label: '8',   color: '#ac2492' },
            { id: 'tariff-9', label: '9',   color: '#be3785' },
            { id: 'tariff-10', label: '10', color: '#c94c7a' },
            { id: 'tariff-11', label: '11', color: '#d8576c' },
            { id: 'tariff-12', label: '12', color: '#e3685d' },
            { id: 'tariff-13', label: '13', color: '#ec824f' },
            { id: 'tariff-14', label: '14', color: 'hsl(45, 98%, 57%)' },
            { id: 'tariff-15', label: '15', color: '#f5c84e' },
            { id: 'tariff-16', label: '16', color: '#f8e938' },
            { id: 'tariff-17', label: '17', color: '#f0f921' },
        ],
    },
};

// Dynamic Tariff Zones - градация по коду зоны (300-800)
export const DYNAMIC_TARIFF_ZONES_STYLE = {
    zoneCode: {
        property: 'zone_code',
        items: [
            { id: 'zone-300', value: 300, label: '300', color: '#0d0887' },
            { id: 'zone-400', value: 400, label: '400', color: '#6a00a8' },
            { id: 'zone-500', value: 500, label: '500', color: '#b12a90' },
            { id: 'zone-600', value: 600, label: '600', color: '#e16462' },
            { id: 'zone-700', value: 700, label: '700', color: '#fca636' },
            { id: 'zone-800', value: 800, label: '800', color: '#f0f921' },
        ],
    },
};

/**
 * Стили для COLORED_ITEMS легенд (категориальные данные)
 * Используются для ColoredItemsLegend компонентов
 */

// POI - категории точек интереса
export const POI_STYLE = {
    categories: [
        { value: 'food', label: defineMessage({ message: 'Еда' }), color: '#FF4081' },
        { value: 'shop', label: defineMessage({ message: 'Магазины' }), color: '#FFE57F' },
        { value: 'leisure', label: defineMessage({ message: 'Развлечения' }), color: '#CCFF90' },
        { value: 'office', label: defineMessage({ message: 'Офисы' }), color: '#84FFFF' },
        { value: 'service', label: defineMessage({ message: 'Сервис' }), color: 'yellow' },
        { value: 'schools_and_kindergartens', label: defineMessage({ message: 'Образование' }), color: '#E6E6FA' },
        { value: 'higher_education', label: defineMessage({ message: 'Высшее образование' }), color: 'orange' },
        { value: 'delivery', label: defineMessage({ message: 'Доставка' }), color: '#d54dff' },
        { value: 'tourism', label: defineMessage({ message: 'Туризм' }), color: 'red' },
        { value: 'transport', label: defineMessage({ message: 'Транспорт' }), color: '#448AFF' },
        { value: 'subway_entrance', label: defineMessage({ message: 'Вход в метро' }), color: '#448AFF' },
        { value: 'parkings', label: defineMessage({ message: 'Велопарковки' }), color: '#808000' },
    ],
};

// POI - источник данных (DA-7087): тоглы OSM / AllThePlaces.xyz
export const POI_SOURCE_STYLE = {
    sources: [
        { value: 'osm', label: defineMessage({ message: 'OpenStreetMap' }) },
        { value: 'atp', label: defineMessage({ message: 'AllThePlaces.xyz' }), icon: LEGEND_ICON_TYPES.RING },
    ],
};

// New Buildings - типы новых зданий
export const NEW_BUILDINGS_STYLE = {
    types: [
        { id: 'standard', color: '#cf00cc', label: defineMessage({ message: 'Типовой' }) },
        { id: 'comfort',  color: '#3d5cf6', label: defineMessage({ message: 'Комфорт' }) },
        { id: 'business', color: '#0dd75b', label: defineMessage({ message: 'Бизнес' }) },
        { id: 'elite',    color: '#F7FE2E', label: defineMessage({ message: 'Элитный' }) },
        { id: 'other',    color: 'red', label: defineMessage({ message: 'Другое' }) },
    ],
};

// Parking Status - статусы парковок
export const PARKING_STATUS_STYLE = {
    statuses: {
        items: [
            { id: 'free', value: 'free', label: defineMessage({ message: 'Свободна' }), color: '#00FF00' },
            { id: 'paid', value: 'paid', label: defineMessage({ message: 'Платная' }), color: '#FFA500' },
            { id: 'occupied', value: 'occupied', label: defineMessage({ message: 'Занята' }), color: '#FF0000' },
        ],
    },
};

// Classified Parking Types - классы парковок
export const CLASS_PARKING_TYPES_STYLE = {
    types: {
        items: [
            { id: 'top', value: 'top', label: defineMessage({ message: 'Топовые' }), color: 'grey' },
            { id: 'middle', value: 'middle', label: defineMessage({ message: 'Средние' }), color: 'grey' },
            { id: 'min', value: 'min', label: defineMessage({ message: 'Минимальные' }), color: '#0dd75b' },
            { id: 'less_5', value: 'less_5', label: defineMessage({ message: 'Стартов и финишей меньше 5' }), color: 'pink' },
            { id: 'zero', value: 'zero', label: defineMessage({ message: 'Нулевые' }), color: 'white' },
        ],
    },
};

// Landuse - использование земли (плохое/хорошее/частный сектор)
export const LANDUSE_STYLE = {
    categories: {
        items: [
            { id: 'bad',     value: 'bad',      color: '#f891ff', label: defineMessage({ message: 'плохое' }) },
            { id: 'good',    value: 'good',     color: '#ff9100', label: defineMessage({ message: 'хорошее' }) },
            { id: 'private', value: 'private',  color: 'yellow', label: defineMessage({ message: 'частный сектор' }) },
        ],
    },
};

// DA-15980 — тот же слой на источнике dwh.urban_zones_classified (типология территорий).
// Первые три категории и их цвета повторяют LANDUSE_STYLE: сравнивать источники имеет смысл
// только при одинаковой палитре. Четвёртая — то, чего у OSM-варианта нет в принципе.
export const LANDUSE_NEW_STYLE = {
    categories: {
        items: [
            { id: 'bad',          value: 'bad',          color: '#f891ff', label: defineMessage({ message: 'плохое' }) },
            { id: 'good',         value: 'good',         color: '#ff9100', label: defineMessage({ message: 'хорошее' }) },
            { id: 'private',      value: 'private',      color: 'yellow',  label: defineMessage({ message: 'частный сектор' }) },
            { id: 'unclassified', value: 'unclassified', color: '#9CA3AF', label: defineMessage({ message: 'не классифицировано' }) },
        ],
    },
};

// Population - плотность населения по ячейкам
export const POPULATION_STYLE = {
    density: {
        items: [
            { id: 'pop-1', value: '<1000',  color: '#30123b', label: defineMessage({ message: '{0} человек', values: { 0: '< 1000' } }) },
            { id: 'pop-2', value: '≥1000',  color: '#4664da', label: defineMessage({ message: '{0} человек', values: { 0: '≥ 1000' } }) },
            { id: 'pop-3', value: '≥2000',  color: '#1ad2d2', label: defineMessage({ message: '{0} человек', values: { 0: '≥ 2000' } }) },
            { id: 'pop-4', value: '≥3000',  color: '#88ff4e', label: defineMessage({ message: '{0} человек', values: { 0: '≥ 3000' } }) },
            { id: 'pop-5', value: '≥4000',  color: '#d4e735', label: defineMessage({ message: '{0} человек', values: { 0: '≥ 4000' } }) },
            { id: 'pop-6', value: '≥5000',  color: '#fea732', label: defineMessage({ message: '{0} человек', values: { 0: '≥ 5000' } }) },
            { id: 'pop-7', value: '≥10000', color: '#7a0403', label: defineMessage({ message: '{0} человек', values: { 0: '≥ 10000' } }) },
        ],
    },
};

// Slope - уклоны рельефа (в градусах)
export const SLOPE_STYLE = {
    slopes: {
        items: [
            { id: 'slope-0-5',      value: '0–5°',      color: '#228B22', label: defineMessage({ message: '0–5°' }) },
            { id: 'slope-5-15',     value: '5–15°',     color: '#FFFF00', label: defineMessage({ message: '5–15°' }) },
            { id: 'slope-15-30',    value: '15–30°',    color: '#FFA500', label: defineMessage({ message: '15–30°' }) },
            { id: 'slope-30-45',    value: '30–45°',    color: '#FF0000', label: defineMessage({ message: '30–45°' }) },
            { id: 'slope-45',       value: '45°+',      color: '#8B008B', label: defineMessage({ message: '45°+' }) },
        ],
    },
};

// Functional Areas - функциональные зоны
export const FUNCTIONAL_AREAS_STYLE = {
    zones: [
        {
            value: 'LIMITED',
            label: defineMessage({ message: 'Зона катания' }),
            color: 'green',
        },
        {
            value: 'FORBIDDEN',
            label: defineMessage({ message: 'Зона запрета катания' }),
            color: 'red',
        },
        {
            value: 'PROPOSED_FORBIDDEN',
            label: defineMessage({ message: 'Предлагаемая зона запрета' }),
            color: 'pink',
        },
        {
            value: 'PERMITTED',
            label: defineMessage({ message: 'Зона парковок' }),
            color: 'yellow',
        },
        {
            value: 'PERMITTED_FICTITIOUS',
            label: defineMessage({ message: 'Зона парковок (фиктивная)' }),
            color: 'yellow',
        },
        {
            value: 'PROPOSED_PERMITTED',
            label: defineMessage({ message: 'Предлагаемая зона парковок' }),
            color: '#00ae9d',
        },
    ],
};

// Restriction Zones - зоны ограничений (из agg_trips_august_mvt)
export const RESTRICTION_ZONES_STYLE = {
    zones: [
        {
            value: 'LIMITED',
            label: defineMessage({ message: 'Зона катания' }),
            color: 'green',
        },
        {
            value: 'FORBIDDEN',
            label: defineMessage({ message: 'Зона запрета катания' }),
            color: 'red',
        },
        {
            value: 'PERMITTED',
            label: defineMessage({ message: 'Зона парковки' }),
            color: 'yellow',
        },
        {
            value: 'STOP_LIMIT',
            label: defineMessage({ message: 'Зона запрета парковки' }),
            color: 'orange',
        },
        {
            value: 'SPEED_LIMIT',
            label: defineMessage({ message: 'Зона ограничения скорости' }),
            color: '#d4a9fc',
        },
    ],
};

// MapBox paint style для Зон ограничений
export const RESTRICTION_ZONES_MAPBOX_PAINT_FILL = {
    'fill-color': [
        'match',
        [ 'get', 'type' ],
        'LIMITED', RESTRICTION_ZONES_STYLE.zones[0].color,
        'FORBIDDEN', RESTRICTION_ZONES_STYLE.zones[1].color,
        'STOP_LIMIT', RESTRICTION_ZONES_STYLE.zones[2].color,
        'PERMITTED', RESTRICTION_ZONES_STYLE.zones[3].color,
        'SPEED_LIMIT', '#d4a9fc', // фиолетовый цвет как в "Зоны ограничения скорости"
        '#cccccc', // default
    ],
    'fill-opacity': [
        'case',
        // Если invisible=true, то заливка прозрачна независимо от типа
        [ '==', [ 'get', 'invisible' ], true ], 0,
        // Иначе применяем обычную логику по типу зоны
        [
            'match',
            [ 'get', 'type' ],
            'SPEED_LIMIT', 0.3, // прозрачность как в оригинальном слое
            'FORBIDDEN', 0.3, // полупрозрачная заливка для зон запрета катания
            'STOP_LIMIT', 0.3, // полупрозрачная заливка для зон запрета парковки
            0, // остальные зоны без заливки
        ],
    ],
    'fill-outline-color': 'black',
};

export const RESTRICTION_ZONES_MAPBOX_PAINT_LINE = {
    'line-color': [
        'match',
        [ 'get', 'type' ],
        'LIMITED', RESTRICTION_ZONES_STYLE.zones[0].color,
        'FORBIDDEN', RESTRICTION_ZONES_STYLE.zones[1].color,
        'STOP_LIMIT', RESTRICTION_ZONES_STYLE.zones[2].color,
        'PERMITTED', RESTRICTION_ZONES_STYLE.zones[3].color,
        'SPEED_LIMIT', '#7d71ed', // фиолетовый цвет как в "Зоны ограничения скорости"
        '#cccccc', // default
    ],
    'line-width': [
        'match',
        [ 'get', 'type' ],
        'SPEED_LIMIT', 1.5, // толщина как в оригинальном слое
        'FORBIDDEN', 1.5, // тонкая обводка для зон запрета катания
        'STOP_LIMIT', 1.5, // тонкая обводка для зон запрета парковки
        3, // для остальных зон - более толстая линия
    ],
    'line-opacity': [
        'match',
        [ 'get', 'type' ],
        'SPEED_LIMIT', 0.9, // прозрачность как в оригинальном слое
        'FORBIDDEN', 0.9, // прозрачность для зон запрета катания
        'STOP_LIMIT', 0.9, // прозрачность для зон запрета парковки
        1, // для остальных зон - полная непрозрачность
    ],
    'line-dasharray': [
        'case',
        [ 'get', 'fictitious' ], // если зона фиктивная
        [ 'literal', [ 3, 2 ] ], // пунктирная линия (3px линия, 2px пробел)
        [ 'literal', [ 1 ] ], // сплошная линия
    ],
};

// ============================================================================
// RESTRICTION ZONES V2 - Разбитые на 3 категории для отдельной обработки
// ============================================================================

/**
 * All Zones Style — все 5 типов зон с реальными цветами карты.
 * Используется в RESTRICTION_ZONES_V2_FILTER (легенда/фильтр).
 * color — цвет заливки, borderColor — цвет обводки (как на карте).
 */
export const RESTRICTION_ZONES_V2_STYLE = {
    zones: [
        {
            value: 'LIMITED',
            label: defineMessage({ message: 'Зона катания' }),
            color: '#346931',
            borderColor: '#2d9e27',
        },
        {
            value: 'FORBIDDEN',
            label: defineMessage({ message: 'Зона запрета катания' }),
            color: '#c84849',
            borderColor: '#8e1a1b',
        },
        {
            value: 'PERMITTED',
            label: defineMessage({ message: 'Зона парковки' }),
            color: '#fffb00',
            borderColor: '#eeea00',
        },
        {
            value: 'STOP_LIMIT',
            label: defineMessage({ message: 'Зона запрета парковки' }),
            color: '#a79040',
            borderColor: '#ff7f00',
        },
        {
            value: 'SPEED_LIMIT',
            label: defineMessage({ message: 'Зона ограничения скорости' }),
            color: '#6d65b2',
            borderColor: '#4a4483',
        },
    ],
};

/**
 * Riding Zones - Зоны катания и Зоны запрета катания
 * LIMITED (зелёная) + FORBIDDEN (красная)
 */
export const RESTRICTION_ZONES_V2_RIDING_STYLE = {
    zones: [
        {
            value: 'LIMITED',
            label: defineMessage({ message: 'Зона катания' }),
            color: '#346931',
            borderColor: '#2d9e27',
        },
        {
            value: 'FORBIDDEN',
            label: defineMessage({ message: 'Зона запрета катания' }),
            color: '#c84849',
            borderColor: '#8e1a1b',
            proposedColor: '#c8488a',
            proposedBorderColor: '#971461',
        },
    ],
};

/**
 * Parking Zones - Зоны парковок и Запрет парковки
 * PERMITTED (жёлтая) + STOP_LIMIT (оранжевая)
 */
export const RESTRICTION_ZONES_V2_PARKING_STYLE = {
    zones: [
        {
            value: 'PERMITTED',
            label: defineMessage({ message: 'Зона парковки' }),
            color: '#fffb00',
            borderColor: '#eeea00',
            proposedColor: '#ffe44b',
            proposedBorderColor: '#ffdd1c',
        },
        {
            value: 'STOP_LIMIT',
            label: defineMessage({ message: 'Зона запрета парковки' }),
            color: '#ff7f00',
            borderColor: '#bf670e',
        },
    ],
};

/**
 * Speed Limit Zones - Зоны ограничения скорости
 * SPEED_LIMIT (фиолетовая)
 */
export const RESTRICTION_ZONES_V2_SPEED_STYLE = {
    zones: [
        {
            value: 'SPEED_LIMIT',
            label: defineMessage({ message: 'Зона ограничения скорости' }),
            color: '#6d65b2',
            borderColor: '#4a4483',
        },
    ],
};

// Вспомогательная функция: paint fill для одного типа зоны
const devFillPaint = color => ({
    'fill-color': color,
    'fill-opacity': 0.3,
});

// Вспомогательная функция: paint fill-pattern для fictitious-зон (dot hatching)
export const v2FictitiousFillPatternPaint = patternId => ({
    'fill-pattern': patternId,
    'fill-opacity': 0.8,
});
// Вспомогательная функция: paint line для одного типа зоны
const devLinePaint = color => ({
    'line-color': color,
    'line-width': 2,
    'line-opacity': 1,
});
// Сплошная обводка — для отдельных fictitious line-слоёв
const devSolidLinePaint = color => ({
    'line-color': color,
    'line-width': 2,
    'line-opacity': 1,
});
// Пунктирная обводка — для fictitious line-слоёв (новый стиль: заливка сплошная, обводка пунктирная)
const devDashedLinePaint = color => ({
    ...devSolidLinePaint(color),
    'line-dasharray': [ 3, 1 ],
});

export const RESTRICTION_ZONES_V2_LIMITED_MAPBOX_PAINT_FILL = devFillPaint(RESTRICTION_ZONES_V2_RIDING_STYLE.zones[0].color);
export const RESTRICTION_ZONES_V2_LIMITED_MAPBOX_PAINT_LINE = devLinePaint(RESTRICTION_ZONES_V2_RIDING_STYLE.zones[0].borderColor);
export const RESTRICTION_ZONES_V2_LIMITED_FICTITIOUS_PAINT_FILL = devFillPaint(RESTRICTION_ZONES_V2_RIDING_STYLE.zones[0].color);
export const RESTRICTION_ZONES_V2_LIMITED_FICTITIOUS_PAINT_LINE = devDashedLinePaint(RESTRICTION_ZONES_V2_RIDING_STYLE.zones[0].borderColor);

export const RESTRICTION_ZONES_V2_FORBIDDEN_MAPBOX_PAINT_FILL = {
    'fill-color': [
        'case',
        [ 'match', [ 'to-string', [ 'get', 'proposed' ] ], [ 'true', '1' ], true, false ],
        RESTRICTION_ZONES_V2_RIDING_STYLE.zones[1].proposedColor,
        RESTRICTION_ZONES_V2_RIDING_STYLE.zones[1].color,
    ],
    'fill-opacity': 0.3,
};
export const RESTRICTION_ZONES_V2_FORBIDDEN_MAPBOX_PAINT_LINE = devLinePaint(RESTRICTION_ZONES_V2_RIDING_STYLE.zones[1].borderColor);
export const RESTRICTION_ZONES_V2_FORBIDDEN_PROPOSED_PAINT_LINE = devLinePaint(RESTRICTION_ZONES_V2_RIDING_STYLE.zones[1].proposedBorderColor);
export const RESTRICTION_ZONES_V2_FORBIDDEN_FICTITIOUS_PAINT_FILL = devFillPaint(RESTRICTION_ZONES_V2_RIDING_STYLE.zones[1].color);
export const RESTRICTION_ZONES_V2_FORBIDDEN_FICTITIOUS_PAINT_LINE = devDashedLinePaint(RESTRICTION_ZONES_V2_RIDING_STYLE.zones[1].borderColor);

export const RESTRICTION_ZONES_V2_PERMITTED_MAPBOX_PAINT_FILL = {
    'fill-color': [
        'case',
        [ 'match', [ 'to-string', [ 'get', 'proposed' ] ], [ 'true', '1' ], true, false ],
        RESTRICTION_ZONES_V2_PARKING_STYLE.zones[0].proposedColor,
        RESTRICTION_ZONES_V2_PARKING_STYLE.zones[0].color,
    ],
    'fill-opacity': 0.3,
};
export const RESTRICTION_ZONES_V2_PERMITTED_MAPBOX_PAINT_LINE = devLinePaint(RESTRICTION_ZONES_V2_PARKING_STYLE.zones[0].borderColor);
export const RESTRICTION_ZONES_V2_PERMITTED_PROPOSED_PAINT_LINE = devLinePaint(RESTRICTION_ZONES_V2_PARKING_STYLE.zones[0].proposedBorderColor);
export const RESTRICTION_ZONES_V2_PERMITTED_FICTITIOUS_PAINT_FILL = devFillPaint(RESTRICTION_ZONES_V2_PARKING_STYLE.zones[0].color);
export const RESTRICTION_ZONES_V2_PERMITTED_FICTITIOUS_PAINT_LINE = devDashedLinePaint(RESTRICTION_ZONES_V2_PARKING_STYLE.zones[0].borderColor);

export const RESTRICTION_ZONES_V2_STOP_LIMIT_MAPBOX_PAINT_FILL = devFillPaint(RESTRICTION_ZONES_V2_PARKING_STYLE.zones[1].color);
export const RESTRICTION_ZONES_V2_STOP_LIMIT_MAPBOX_PAINT_LINE = devLinePaint(RESTRICTION_ZONES_V2_PARKING_STYLE.zones[1].borderColor);
export const RESTRICTION_ZONES_V2_STOP_LIMIT_FICTITIOUS_PAINT_FILL = devFillPaint(RESTRICTION_ZONES_V2_PARKING_STYLE.zones[1].color);
export const RESTRICTION_ZONES_V2_STOP_LIMIT_FICTITIOUS_PAINT_LINE = devDashedLinePaint(RESTRICTION_ZONES_V2_PARKING_STYLE.zones[1].borderColor);

export const RESTRICTION_ZONES_V2_SPEED_MAPBOX_PAINT_FILL = devFillPaint(RESTRICTION_ZONES_V2_SPEED_STYLE.zones[0].color);
export const RESTRICTION_ZONES_V2_SPEED_MAPBOX_PAINT_LINE = devLinePaint(RESTRICTION_ZONES_V2_SPEED_STYLE.zones[0].borderColor);
export const RESTRICTION_ZONES_V2_SPEED_FICTITIOUS_PAINT_FILL = devFillPaint(RESTRICTION_ZONES_V2_SPEED_STYLE.zones[0].color);
export const RESTRICTION_ZONES_V2_SPEED_FICTITIOUS_PAINT_LINE = devDashedLinePaint(RESTRICTION_ZONES_V2_SPEED_STYLE.zones[0].borderColor);

// Внутренние контуры (inner_boundary) — тот же цвет, но толщина 0.5
const devInnerLinePaint = color => ({
    'line-color': color,
    'line-width': 0.5,
    'line-opacity': 1,
});

export const RESTRICTION_ZONES_V2_LIMITED_INNER_PAINT_LINE    = devInnerLinePaint(RESTRICTION_ZONES_V2_RIDING_STYLE.zones[0].borderColor);
export const RESTRICTION_ZONES_V2_FORBIDDEN_INNER_PAINT_LINE  = devInnerLinePaint(RESTRICTION_ZONES_V2_RIDING_STYLE.zones[1].borderColor);
export const RESTRICTION_ZONES_V2_PERMITTED_INNER_PAINT_LINE  = devInnerLinePaint(RESTRICTION_ZONES_V2_PARKING_STYLE.zones[0].borderColor);
export const RESTRICTION_ZONES_V2_STOP_LIMIT_INNER_PAINT_LINE = devInnerLinePaint(RESTRICTION_ZONES_V2_PARKING_STYLE.zones[1].borderColor);
export const RESTRICTION_ZONES_V2_SPEED_INNER_PAINT_LINE      = devInnerLinePaint(RESTRICTION_ZONES_V2_SPEED_STYLE.zones[0].borderColor);

// Parking Radius - расстояния до парковок
export const PARKING_RADIUS_STYLE = {
    distances: {
        items: [
            { id: 'radius-1', value: '<15', color: 'red', label: defineMessage({ message: 'Менее {0} м', values: { 0: 15 } }) },
            { id: 'radius-2', value: '15-25', color: 'white', label: defineMessage({ message: 'от {0} м до {1} м', values: { 0: 15, 1: 25 } }) },
            { id: 'radius-3', value: '≥25', color: 'green', label: defineMessage({ message: '{0} м', values: { 0: '≥ 25' } }) },
            { id: 'radius-4', value: '>25', color: 'yellow', label: defineMessage({ message: 'Более {0} м', values: { 0: 25 } }) },
        ],
    },
};

// Classified Parking - классификация парковок (Дефицит/Баланс/Профицит)
export const CLASSIFIED_PARKING_STYLE = {
    classification: [
        { id: 'deficit', color: 'red', label: defineMessage({ message: 'Дефицит' }), value: 'deficit' },
        { id: 'balance', color: 'yellow', label: defineMessage({ message: 'Баланс' }), value: 'balance' },
        { id: 'proficit', color: 'green', label: defineMessage({ message: 'Профицит' }), value: 'proficit' },
    ],
    classItems: [
        {
            value: 'top',
            label: defineMessage({ message: 'Топовые' }),
            color: 'grey',
        },
        {
            value: 'middle',
            label: defineMessage({ message: 'Средние' }),
            color: 'grey',
        },
        {
            value: 'min',
            label: defineMessage({ message: 'Минимальные' }),
            color: '#0dd75b',
        },
        {
            value: 'less_5',
            label: defineMessage({ message: 'Менее 5 стартов и финишей' }),
            color: 'pink',
        },
        {
            value: 'zero',
            label: defineMessage({ message: 'Нулевые' }),
            color: 'white',
        },
    ],
};

// Bike Paths - велодорожки (город vs OSM) и велополосы на проезжей части (DA-15814).
// Цвета используются и в paint слоя «Велодорожки», и в легенде.
export const BIKE_PATH_STYLE = {
    types: [
        { id: 'city-bikepath', color: '#50C1E9', label: defineMessage({ message: 'Велодорожка (данные от города)' }), value: 'city' },
        { id: 'osm-bikepath', color: '#03fc98', label: defineMessage({ message: 'Велодорожка (данные OSM)' }), value: 'osm' },
        { id: 'osm-bikelane', color: '#1B7A3E', label: defineMessage({ message: 'Велополоса (данные OSM)' }), value: 'osm-lane' },
    ],
};

// DA-14933 — треки исполнителей: цвет по способу перемещения
export const SCR_TRACKS_STYLE = {
    // Градуировка трека по роли исполнителя (WCMA): ranger — пеший ребаланс,
    // service_car — сервисная машина, charger — замена АКБ. Цвета используются
    // и в paint слоя, и в легенде/контроле выключения ролей.
    roles: [
        { id: 'scr-ranger',      color: '#2e7d32', label: defineMessage({ message: 'Рейнджер' }),       value: 'ranger' },
        { id: 'scr-service-car', color: '#1565c0', label: defineMessage({ message: 'Сервисная машина' }), value: 'service_car' },
        { id: 'scr-charger',     color: '#e65100', label: defineMessage({ message: 'Зарядка АКБ' }),     value: 'charger' },
        { id: 'scr-coordinator', color: '#6a1b9a', label: defineMessage({ message: 'Координатор' }),     value: 'coordinator' },
    ],
};

// DA-14933 — инфраструктура снаппинга концов сегментов (отдельный точечный подслой scr_infra):
// парковки (VERIFIED) с z12, СЦ/зарядные шкафы/станции — на всех зумах. Цвета — в paint точек и легенде.
export const SCR_INFRA_STYLE = {
    kinds: [
        { id: 'scr-infra-parking',         color: '#455a64', label: defineMessage({ message: 'Парковки' }),          value: 'parking' },
        { id: 'scr-infra-service-center',  color: '#d81b60', label: defineMessage({ message: 'Сервисные центры' }),   value: 'service_center' },
        { id: 'scr-infra-warehouse',       color: '#8d6e63', label: defineMessage({ message: 'Временные склады' }),   value: 'warehouse' },
        { id: 'scr-infra-battery-cabinet', color: '#fdd835', label: defineMessage({ message: 'Зарядные шкафы' }),     value: 'battery_cabinet' },
        { id: 'scr-infra-charge-station',  color: '#00acc1', label: defineMessage({ message: 'Зарядные станции' }),   value: 'charge_station' },
    ],
};

// DA-15040 — ДТП с участием СИМ (ГИБДД). Раскраска точек переключается между категоризациями
// (тип инцидента / оператор / нарушитель); цвета используются в paint слоя и в легенде панели.
export const SIM_ACCIDENTS_STYLE = {
    fallbackColor: '#9e9e9e',
    fallbackLabel: defineMessage({ message: 'Нет данных' }),
    // Доступные категоризации (порядок = порядок в переключателе панели).
    categorizations: [
        { key: 'accident_type', label: defineMessage({ message: 'Тип инцидента' }) },
        { key: 'operator',      label: defineMessage({ message: 'Оператор' }) },
        { key: 'offender',      label: defineMessage({ message: 'Нарушитель' }) },
    ],
    // Категории по каждому атрибуту: value (как в данных) + цвет + подпись.
    categories: {
        // eslint-disable-next-line camelcase -- ключ = имя атрибута/колонки в данных
        accident_type: [
            { value: 'Наезд на пешехода',                                 color: '#e53935', label: defineMessage({ message: 'Наезд на пешехода' }) },
            { value: 'Столкновение',                                      color: '#fb8c00', label: defineMessage({ message: 'Столкновение' }) },
            { value: 'Опрокидывание',                                     color: '#fdd835', label: defineMessage({ message: 'Опрокидывание' }) },
            { value: 'Наезд на лицо, использующее для передвижения СИМ',   color: '#8e24aa', label: defineMessage({ message: 'Наезд на лицо на СИМ' }) },
            { value: 'Наезд на велосипедиста',                            color: '#43a047', label: defineMessage({ message: 'Наезд на велосипедиста' }) },
            { value: 'Наезд на препятствие',                              color: '#00acc1', label: defineMessage({ message: 'Наезд на препятствие' }) },
            { value: 'Наезд на стоящее т/с',                              color: '#6d4c41', label: defineMessage({ message: 'Наезд на стоящее т/с' }) },
            { value: 'Падение пассажира',                                 color: '#5c6bc0', label: defineMessage({ message: 'Падение пассажира' }) },
        ],
        operator: [
            { value: 'Яндекс',              color: operatorTypeColor('Yandex'),    label: defineMessage({ message: 'Яндекс' }) },
            { value: 'Юрент',               color: operatorTypeColor('Urent'),     label: defineMessage({ message: 'Юрент' }) },
            { value: 'ВУШ',                 color: operatorTypeColor('Whoosh'),    label: defineMessage({ message: 'ВУШ' }) },
            { value: 'ММ-тех',              color: operatorTypeColor('ММ-тех'),    label: defineMessage({ message: 'ММ-тех' }) },
            { value: 'Ситибайк',            color: operatorTypeColor('SmartBike'), label: defineMessage({ message: 'Ситибайк' }) },
            { value: 'Аренда, нет данных',  color: '#9e9e9e', label: defineMessage({ message: 'Аренда, нет данных' }) },
        ],
        offender: [
            { value: 'Водитель (СИМ аренда)', color: '#e53935', label: defineMessage({ message: 'Водитель (СИМ аренда)' }) },
            { value: 'Водитель',              color: '#1565c0', label: defineMessage({ message: 'Водитель' }) },
            { value: 'Пешеход',               color: '#fb8c00', label: defineMessage({ message: 'Пешеход' }) },
        ],
    },
    // Размерная шкала точки = число пострадавших (ранено + погибло); см. SIM_ACCIDENTS_RADIUS.
    // Радиусы легенды считаются от minRadius/maxRadius в конфиге легенды (2…13 px).
    victims: {
        items: [
            { id: 'victims-1', value: 1, label: defineMessage({ message: '1' }) },
            { id: 'victims-2', value: 2, label: defineMessage({ message: '2' }) },
        ],
    },
};

// Supposed Bike Paths - предлагаемые велодорожки (этапы)
export const SUPPOSED_BIKE_PATH_STYLE = {
    stages: [
        { id: 'stage-1', color: '#ffff0d', label: defineMessage({ message: 'Этап {0}', values: { 0: 1 } }), value: 'stage-1' },
        { id: 'stage-2', color: '#50C1E9', label: defineMessage({ message: 'Этап {0}', values: { 0: 2 } }), value: 'stage-2' },
        { id: 'stage-3', color: '#EAEAEA', label: defineMessage({ message: 'Этап {0}', values: { 0: 3 } }), value: 'stage-3' },
    ],
};

// Clustered Taps - кластеризированные тапы пользователей (Local Moran's I)
export const CLUSTERED_TAPS_STYLE = {
    clusters: [
        { id: 'hh', color: 'red', label: defineMessage({ message: 'HH' }), value: 'hh' },
        { id: 'hl', color: '#ffb2b2', label: defineMessage({ message: 'HL' }), value: 'hl' },
        { id: 'lh', color: '#89CFF0', label: defineMessage({ message: 'LH' }), value: 'lh' },
        { id: 'll', color: '#0096FF', label: defineMessage({ message: 'LL' }), value: 'll' },
    ],
};

// DA-15433 — предложения по парковкам. Ступени — перцентиль спроса ВНУТРИ города,
// а не абсолютное число людей: спрос в Москве и во Владимире отличается на два порядка
// и по абсолютной шкале несопоставим.
export const PARKING_PROPOSALS_STYLE = {
    demand: [
        { id: 'top', color: '#fcfdbf', label: defineMessage({ message: 'Наивысший (топ 20%)' }), value: 'top' },
        { id: 'high', color: '#f4695c', label: defineMessage({ message: 'Высокий' }), value: 'high' },
        { id: 'mid', color: '#c23b75', label: defineMessage({ message: 'Средний' }), value: 'mid' },
        { id: 'low', color: '#792282', label: defineMessage({ message: 'Пониженный' }), value: 'low' },
        { id: 'bottom', color: '#2c1a72', label: defineMessage({ message: 'Низкий (нижние 20%)' }), value: 'bottom' },
        // Обводка у клякс того же цвета, что и заливка — отличает их только пунктир,
        // поэтому образец нейтральный, а не «цвет кляксы».
        { id: 'oversized', color: '#9ca3af', label: defineMessage({ message: 'Пунктирная обводка — слишком крупный кластер' }), value: 'oversized' },
    ],
};

// Clustered Opens - кластеризированные открытия пользователей
export const CLUSTERED_OPENS_STYLE = {
    clusters: {
        items: [
            { id: 'hh', value: 'hh', label: defineMessage({ message: 'HH' }), color: 'red' },
            { id: 'hl', value: 'hl', label: defineMessage({ message: 'HL' }), color: '#ffb2b2' },
            { id: 'lh', value: 'lh', label: defineMessage({ message: 'LH' }), color: '#89CFF0' },
            { id: 'll', value: 'll', label: defineMessage({ message: 'LL' }), color: '#0096FF' },
        ],
    },
};

// Aggregated User Opens - 7 дней
export const AGG_USER_OPENS_7DAYS_STYLE = {
    ranges: {
        items: [
            { id: 'opens-1', value: '<200',         color: 'hsla(242, 89%, 28%, 0.5)', label: defineMessage({ message: '< 200' }) },
            { id: 'opens-2', value: '200-500',      color: '#792282', label: defineMessage({ message: 'от {0} до {1}', values: { 0: 200, 1: 500 } }) },
            { id: 'opens-3', value: '500-800',      color: '#c23b75', label: defineMessage({ message: 'от {0} до {1}', values: { 0: 500, 1: 800 } }) },
            { id: 'opens-4', value: '800-1200',     color: '#f4695c', label: defineMessage({ message: 'от {0} до {1}', values: { 0: 800, 1: 1200 } }) },
            { id: 'opens-5', value: '1200-2000',    color: '#feb47b', label: defineMessage({ message: 'от {0} до {1}', values: { 0: 1200, 1: 2000 } }) },
            { id: 'opens-6', value: '>2000',        color: '#fcfdbf', label: defineMessage({ message: '> 2000' }) },
        ],
    },
};

// Aggregated User Opens - 30 дней
export const AGG_USER_OPENS_30DAYS_STYLE = {
    ranges: {
        items: [
            { id: 'opens-1', value: '<500',         color: 'hsla(242, 89%, 28%, 0.5)', label: defineMessage({ message: '< 500' }) },
            { id: 'opens-2', value: '500-1000',     color: '#792282', label: defineMessage({ message: 'от {0} до {1}', values: { 0: 500, 1: 1000 } }) },
            { id: 'opens-3', value: '1000-2000',    color: '#c23b75', label: defineMessage({ message: 'от {0} до {1}', values: { 0: 1000, 1: 2000 } }) },
            { id: 'opens-4', value: '2000-3000',    color: '#f4695c', label: defineMessage({ message: 'от {0} до {1}', values: { 0: 2000, 1: 3000 } }) },
            { id: 'opens-5', value: '3000-5000',    color: '#feb47b', label: defineMessage({ message: 'от {0} до {1}', values: { 0: 3000, 1: 5000 } }) },
            { id: 'opens-6', value: '>5000',        color: '#fcfdbf', label: defineMessage({ message: '> 5000' }) },
        ],
    },
};

// Aggregated User Taps Zero Starts - 7 дней
export const AGG_USER_TAPS_ZERO_STARTS_7DAYS_STYLE = {
    ranges: {
        items: [
            { id: 'taps-1', value: '<30',         color: 'hsla(242, 89%, 28%, 0.5)', label: defineMessage({ message: '< 30' }) },
            { id: 'taps-2', value: '30-70',       color: '#792282', label: defineMessage({ message: 'от {0} до {1}', values: { 0: 30, 1: 70 } }) },
            { id: 'taps-3', value: '70-100',      color: '#c23b75', label: defineMessage({ message: 'от {0} до {1}', values: { 0: 70, 1: 100 } }) },
            { id: 'taps-4', value: '100-150',     color: '#f4695c', label: defineMessage({ message: 'от {0} до {1}', values: { 0: 100, 1: 150 } }) },
            { id: 'taps-5', value: '150-300',     color: '#feb47b', label: defineMessage({ message: 'от {0} до {1}', values: { 0: 150, 1: 300 } }) },
            { id: 'taps-6', value: '>300',       color: '#fcfdbf', label: defineMessage({ message: '> 300' }) },
        ],
    },
};

// Конверсия из регистраций в старты (DA-13570)
export const REG_TO_TRIP_CONVERSION_STYLE = {
    ranges: {
        items: [
            { id: 'conv-6', value: '>0.5',    color: '#ffffb2', label: defineMessage({ message: '> 50%' }) },
            { id: 'conv-5', value: '0.4-0.5', color: '#fecc5c', label: defineMessage({ message: 'от {0} до {1}', values: { 0: '40%', 1: '50%' } }) },
            { id: 'conv-4', value: '0.3-0.4', color: '#fd8d3c', label: defineMessage({ message: 'от {0} до {1}', values: { 0: '30%', 1: '40%' } }) },
            { id: 'conv-3', value: '0.2-0.3', color: '#f03b20', label: defineMessage({ message: 'от {0} до {1}', values: { 0: '20%', 1: '30%' } }) },
            { id: 'conv-2', value: '0.1-0.2', color: '#bd0026', label: defineMessage({ message: 'от {0} до {1}', values: { 0: '10%', 1: '20%' } }) },
            { id: 'conv-1', value: '<0.1',    color: '#67000d', label: defineMessage({ message: '< 10%' }) },
        ],
    },
};

// Aggregated User Taps Zero Starts - 30 дней
export const AGG_USER_TAPS_ZERO_STARTS_30DAYS_STYLE = {
    ranges: {
        items: [
            { id: 'taps-1', value: '<400',       color: 'hsla(242, 89%, 28%, 0.5)', label: defineMessage({ message: '< 400' }) },
            { id: 'taps-2', value: '400-1000',   color: '#792282', label: defineMessage({ message: 'от {0} до {1}', values: { 0: 400, 1: 1000 } }) },
            { id: 'taps-3', value: '1000-2000',  color: '#c23b75', label: defineMessage({ message: 'от {0} до {1}', values: { 0: 1000, 1: 2000 } }) },
            { id: 'taps-4', value: '2000-3000',  color: '#f4695c', label: defineMessage({ message: 'от {0} до {1}', values: { 0: 2000, 1: 3000 } }) },
            { id: 'taps-5', value: '3000-4000',  color: '#feb47b', label: defineMessage({ message: 'от {0} до {1}', values: { 0: 3000, 1: 4000 } }) },
            { id: 'taps-6', value: '>4000',      color: '#fcfdbf', label: defineMessage({ message: '> 4000' }) },
        ],
    },
};

// Aggregated User Opens to Starts Ratio
export const AGG_USER_OPENS_TO_STARTS_STYLE = {
    ratios: {
        items: [
            { id: 'ratio-1', value: '<3',       color: '#03559F', label: defineMessage({ message: '< 3' }) },
            { id: 'ratio-2', value: '3-6',      color: '#792282', label: defineMessage({ message: 'от {0} до {1}', values: { 0: 3, 1: 6 } }) },
            { id: 'ratio-3', value: '6-12',     color: '#c23b75', label: defineMessage({ message: 'от {0} до {1}', values: { 0: 6, 1: 12 } }) },
            { id: 'ratio-4', value: '12-50',    color: '#f4695c', label: defineMessage({ message: 'от {0} до {1}', values: { 0: 12, 1: 50 } }) },
            { id: 'ratio-5', value: '50-100',   color: '#feb47b', label: defineMessage({ message: 'от {0} до {1}', values: { 0: 50, 1: 100 } }) },
            { id: 'ratio-6', value: '>100',     color: '#fcfdbf', label: defineMessage({ message: '> 100' }) },
        ],
    },
};

// Aggregated Diff Start/Finish - разница между стартами и финишами
export const AGGREGATED_DIFF_START_FINISH_STYLE = {
    differences: {
        items: [
            { id: 'diff-1', value: 'finish-large', color: '#d7191c', label: defineMessage({ message: 'Финишей значительно больше (на ≥ 50)' }) },
            { id: 'diff-2', value: 'finish-small', color: '#fdae61', label: defineMessage({ message: 'Финишей больше (на 5 - 50)' }) },
            { id: 'diff-3', value: 'balance',      color: '#ffffbf', label: defineMessage({ message: 'Баланс стартов и финишей' }) },
            { id: 'diff-4', value: 'start-small',  color: '#abd9e9', label: defineMessage({ message: 'Стартов больше (на 5 - 50)' }) },
            { id: 'diff-5', value: 'start-large',  color: '#2c7bb6', label: defineMessage({ message: 'Стартов значительно больше (на ≥ 50)' }) },
        ],
    },
};

// Aggregated Diff Start/Finish August - разница между стартами и финишами (август)
export const AGGREGATED_DIFF_START_FINISH_AUGUST_STYLE = {
    differences: {
        items: [
            { id: 'diff-1', value: 'finish-large', color: '#d7191c', label: defineMessage({ message: 'Финишей значительно больше (на ≥ 100)' }) },
            { id: 'diff-2', value: 'finish-med',   color: '#f17c4a', label: defineMessage({ message: 'Финишей больше (на 50 - 100)' }) },
            { id: 'diff-3', value: 'finish-small', color: '#fec980', label: defineMessage({ message: 'Финишей больше (на 15 - 50)' }) },
            { id: 'diff-4', value: 'balance',      color: '#ffffbf', label: defineMessage({ message: 'Разница стартов/финишей менее 15' }) },
            { id: 'diff-5', value: 'start-small',  color: '#c7e9ad', label: defineMessage({ message: 'Стартов больше (на 15 - 50)' }) },
            { id: 'diff-6', value: 'start-med',    color: '#80bfac', label: defineMessage({ message: 'Стартов больше (на 50 - 100)' }) },
            { id: 'diff-7', value: 'start-large',  color: '#2b83ba', label: defineMessage({ message: 'Стартов значительно больше (на ≥ 100)' }) },
        ],
    },
};

// Aggregated Failed Trips - доля неудачных поездок
export const AGG_FAILED_TRIPS_7DAYS_STYLE = {
    percentages: {
        items: [
            { id: 'failed-1', value: '<10%',    color: '#4620B299', label: defineMessage({ message: '< 10%' }) },
            { id: 'failed-2', value: '10-30%',  color: '#792282', label: defineMessage({ message: 'от {0}% до {1}%', values: { 0: 10, 1: 30 } }) },
            { id: 'failed-3', value: '30-50%',  color: '#c23b75', label: defineMessage({ message: 'от {0}% до {1}%', values: { 0: 30, 1: 50 } }) },
            { id: 'failed-4', value: '50-70%',  color: '#f4695c', label: defineMessage({ message: 'от {0}% до {1}%', values: { 0: 50, 1: 70 } }) },
            { id: 'failed-5', value: '70-100%', color: '#feb47b', label: defineMessage({ message: 'от {0}% до {1}%', values: { 0: 70, 1: 100 } }) },
            { id: 'failed-6', value: '>100%',   color: '#fcfdbf', label: defineMessage({ message: '> 100%' }) },
        ],
    },
};

// DA-13733: загрузка зарядных объектов (martin.charging_objects_load) — оборот слота от потолка
// 7 зарядов в сутки; оборот по счётчикам электроэнергии, у объектов без счётчика — по пешей модели
export const CHARGING_OBJECTS_LOAD_STYLE = {
    utilization: {
        items: [
            { id: 'util-lt30', value: 'lt30', color: '#4575b4', label: defineMessage({ message: '< 30% (до 2 зарядов на слот)' }), hint: defineMessage({ message: 'Резерв: слот заряжает меньше 2,1 АКБ в сутки при потолке 7. Кандидат на перенос слотов к перегруженным узлам.' }) },
            { id: 'util-30-60', value: '30-60', color: '#1a9850', label: defineMessage({ message: '30–60% (2–4 заряда)' }), hint: defineMessage({ message: 'Нормальная работа: 2,1–4,2 заряда на слот в сутки, есть запас на пиковые дни.' }) },
            { id: 'util-60-80', value: '60-80', color: '#fdae61', label: defineMessage({ message: '60–80% (4–5,5 заряда)' }), hint: defineMessage({ message: 'Высокая загрузка: 4,2–5,6 заряда на слот в сутки. В пиковые дни объект подходит к пределу.' }) },
            { id: 'util-gte80', value: 'gte80', color: '#d7301f', label: defineMessage({ message: '≥ 80% (от 5,5 заряда)' }), hint: defineMessage({ message: 'У предела: от 5,6 заряда на слот в сутки при потолке 7 (уровень Мытищ и Рязанского проспекта). Кандидат на расширение или разгрузку соседними шкафами.' }) },
        ],
    },
};

// DA-13733: предложения по развитию сети (martin.charging_network_proposals). open — точка
// кандидата, остальные — кольцо вокруг действующего объекта.
export const CHARGING_PROPOSALS_STYLE = {
    kinds: {
        items: [
            { id: 'prop-open', value: 'open', color: '#00a3a3', label: defineMessage({ message: 'Новый объект для увеличения мощности' }), hint: defineMessage({ message: 'Спрос некому взять: рядом (3 км пешком) нет недогруженного объекта, или место разгружает перегруженный объект в пределах 3 км. Место с наибольшим спросом на замены вне пешей доступности (1,5 км) действующих объектов. Зона места — пешая изохрона 1,5 км; в попапе — замен в сутки и слотов при обороте 4, при наведении — ожидаемая зона. Это район поиска, а не адрес.' }) },
            { id: 'prop-open-walk', value: 'open_walk', color: '#e7298a', label: defineMessage({ message: 'Новый объект для улучшения пешей доступности' }), hint: defineMessage({ message: 'Спрос вне пешей доступности есть, но в 3 км пешком стоит объект с загрузкой ниже 60%: мощности хватает, новое место лишь приблизит зарядку к чарджерам. Приоритет ниже, чем у мест под новую мощность.' }) },
            { id: 'prop-expand', value: 'expand', color: '#FF7033', label: defineMessage({ message: 'Расширить станцию' }), hint: defineMessage({ message: 'Станция или СЦ с загрузкой от 80% потолка: в пиковые дни объект у предела. Добавить слоты; в попапе — сколько слотов нужно для оборота 5.' }) },
            { id: 'prop-offload', value: 'offload', color: '#FDB92E', label: defineMessage({ message: 'Разгрузить шкаф новым объектом' }), hint: defineMessage({ message: 'Шкаф с загрузкой от 75% потолка. Ёмкость шкафа не расширяется, поэтому разгрузить его можно только новым объектом рядом; в попапе — ближайшее предложенное место.' }) },
            { id: 'prop-shrink', value: 'shrink', color: '#9467bd', label: defineMessage({ message: 'Сократить слоты' }), hint: defineMessage({ message: 'При обороте 4 заряда на слот лишние 40 и больше слотов. Там, где хватает 48 слотов, станцию можно заменить шкафом.' }) },
            { id: 'prop-close', value: 'close', color: '#000000', label: defineMessage({ message: 'Кандидат на закрытие' }), hint: defineMessage({ message: 'Загрузка ниже 30%, соседи пешком перехватывают от 35% замен и после перекладки остаются не выше 60%.' }) },
        ],
    },
};

// DA-13733: второй вариант предложений — доступность 15 мин на самокате в форвардинге
// (15 км/ч, 3,75 км по графу треков). Цвета те же, отличаются формулировки подсказок.
export const CHARGING_PROPOSALS_SCOOTER_STYLE = {
    kinds: {
        items: [
            { id: 'prop-s-open', value: 'open', color: '#00a3a3', label: defineMessage({ message: 'Новый объект для увеличения мощности' }), hint: defineMessage({ message: 'Место под спрос, который в 15 минутах на самокате может взять только объект с загрузкой от 60%: от 100 таких замен в сутки. Зона места — 15 мин на самокате (3,75 км по графу); при наведении — ожидаемая зона.' }) },
            { id: 'prop-s-open-walk', value: 'open_walk', color: '#e7298a', label: defineMessage({ message: 'Новый объект для улучшения доступности' }), hint: defineMessage({ message: 'Спрос в основном вне 15 минут на самокате от любого объекта — чаще окраины и Подмосковье.' }) },
            { id: 'prop-s-expand', value: 'expand', color: '#FF7033', label: defineMessage({ message: 'Расширить станцию' }), hint: defineMessage({ message: 'Станция или СЦ с загрузкой от 80% потолка — как в пешем варианте: загрузка от радиуса не зависит.' }) },
            { id: 'prop-s-offload', value: 'offload', color: '#FDB92E', label: defineMessage({ message: 'Разгрузить шкаф новым объектом' }), hint: defineMessage({ message: 'Шкаф с загрузкой от 75%; в попапе — места под новую мощность до 3,75 км, чья зона задевает зону шкафа.' }) },
            { id: 'prop-s-shrink', value: 'shrink', color: '#9467bd', label: defineMessage({ message: 'Сократить слоты' }), hint: defineMessage({ message: 'Как в пешем варианте: при обороте 4 лишние 40 и больше слотов.' }) },
            { id: 'prop-s-close', value: 'close', color: '#000000', label: defineMessage({ message: 'Кандидат на закрытие' }), hint: defineMessage({ message: 'Загрузка ниже 30%, соседи в 15 минутах на самокате перехватывают от 35% замен и после перекладки остаются не выше 60%.' }) },
        ],
    },
};

// DA-13733: согласованный с операционной командой план (variant='plan', 28.09.2026). Kind — как
// у модели плюс объекты «проверить»; новые точки, предложенные на согласовании, отнесены к
// мощности или доступности. Статус решения и комментарий — в попапе.
export const CHARGING_PROPOSALS_PLAN_STYLE = {
    kinds: {
        items: [
            { id: 'plan-open', value: 'open', color: '#00a3a3', label: defineMessage({ message: 'Новый объект для увеличения мощности' }), hint: defineMessage({ message: 'Место под новую мощность: из пешей модели или предложенное на согласовании. Статус и комментарий — в попапе, при наведении — ожидаемая пешая зона.' }) },
            { id: 'plan-open-walk', value: 'open_walk', color: '#e7298a', label: defineMessage({ message: 'Новый объект для улучшения пешей доступности' }), hint: defineMessage({ message: 'Место для шаговой доступности: мощность есть у соседей. При наведении — ожидаемая пешая зона.' }) },
            { id: 'plan-expand', value: 'expand', color: '#FF7033', label: defineMessage({ message: 'Расширить станцию' }), hint: defineMessage({ message: 'Добавить мощность у перегруженной станции — расширением или шкафом рядом, см. попап.' }) },
            { id: 'plan-offload', value: 'offload', color: '#FDB92E', label: defineMessage({ message: 'Разгрузить шкаф новым объектом' }), hint: defineMessage({ message: 'Перегруженный шкаф разгружается новыми точками рядом.' }) },
            { id: 'plan-shrink', value: 'shrink', color: '#9467bd', label: defineMessage({ message: 'Сократить слоты' }), hint: defineMessage({ message: 'Сократить слоты или заменить станцию шкафом.' }) },
            { id: 'plan-close', value: 'close', color: '#000000', label: defineMessage({ message: 'Закрыть или перенести' }), hint: defineMessage({ message: 'Закрыть объект или перенести шкаф в другое место, см. попап.' }) },
            { id: 'plan-review', value: 'review', color: '#7f7f7f', label: defineMessage({ message: 'Проверить' }), hint: defineMessage({ message: 'Нужна аналитика или решение по объекту: недозагрузка, возврат прицепа.' }) },
        ],
    },
};

// Whoosh Parking Capacity - вместимость парковок Whoosh
export const WHOOSH_PARKING_CAPACITY_STYLE = {
    capacity: {
        items: [
            { id: 'capacity-1', value: 'capacity-1', label: defineMessage({ message: '< 5' }), radius: 4 },
            { id: 'capacity-2', value: 'capacity-2', label: defineMessage({ message: '~10' }), radius: 8 },
            { id: 'capacity-3', value: 'capacity-3', label: defineMessage({ message: '> 15' }), radius: 10 },
        ],
    },
};

// Restrictions - Зоны ограничений конкурентов
export const RESTRICTIONS_STYLE = {
    zones: [
        {
            value: 'no_parking',
            label: defineMessage({ message: 'Зона запрета парковки' }),
            color: '#256B87',
        },
        {
            value: 'no_entry',
            label: defineMessage({ message: 'Зона запрета катания' }),
            color: 'red',
        },
        {
            value: 'speed_limit',
            label: defineMessage({ message: 'Ограничение скорости' }),
            color: '#d4a9fc',
        },
        {
            value: 'parking',
            label: defineMessage({ message: 'Зона парковки' }),
            color: 'yellow',
        },
        {
            value: 'other',
            label: defineMessage({ message: 'Прочие зоны' }),
            color: '#808080',
        },
    ],
};

// Speed Limits - Ограничения скорости
export const SPEED_LIMITS_STYLE = {
    limits: [
        {
            value: 'SPEED_LIMIT',
            label: defineMessage({ message: 'Ограничения скорости' }),
            color: '#d4a9fc',
        },
        {
            value: 'STOP_LIMIT',
            label: defineMessage({ message: 'Запрета парковки (без паузы)' }),
            color: '#256B87',
        },
        {
            value: 'STOP_LIMIT_with_pause',
            label: defineMessage({ message: 'Запрета парковки (с паузой)' }),
            color: 'green',
        },
    ],
};

// Downtime Scooters - простой самокатов (в часах)
export const DOWNTIME_SCOOTERS_STYLE = {
    durations: [
        {
            value: '0',
            label: defineMessage({ message: 'менее {0} ч', values: { 0: 2 } }),
            color: 'green',
        },
        {
            value: '1',
            label: defineMessage({ message: 'от {0} до {1} ч', values: { 0: 2, 1: 4 } }),
            color: 'lightgreen',
        },
        {
            value: '2',
            label: defineMessage({ message: 'от {0} до {1} ч', values: { 0: 4, 1: 6 } }),
            color: 'white',
        },
        {
            value: '3',
            label: defineMessage({ message: 'от {0} до {1} ч', values: { 0: 6, 1: 9 } }),
            color: 'yellow',
        },
        {
            value: '4',
            label: defineMessage({ message: 'от {0} до {1} ч', values: { 0: 9, 1: 12 } }),
            color: 'orange',
        },
        {
            value: '5',
            label: defineMessage({ message: 'от {0} до {1} ч', values: { 0: 12, 1: 24 } }),
            color: '#b35500',
        },
        {
            value: '6',
            label: defineMessage({ message: 'от {0} до {1} ч', values: { 0: 24, 1: 48 } }),
            color: 'red',
        },
        {
            value: '7',
            label: defineMessage({ message: 'свыше {0} ч', values: { 0: 48 } }),
            color: '#8B0000',
        },
    ],
    // Размер круга по среднему количеству самокатов на парковке за день
    scooterQuantity: [
        { id: 'qty-1', label: defineMessage({ message: 'Мало' }), value: 'low', radius: 6 },
        { id: 'qty-2', label: defineMessage({ message: 'Среднее' }), value: 'medium', radius: 12 },
        { id: 'qty-3', label: defineMessage({ message: 'Много' }), value: 'high', radius: 18 },
    ],
    // Градиент для легенды "Простой самокатов (48+ ч)"
    downtime48hGradient: [
        { id: 'downtime-1', label: '1', color: 'yellow' },
        { id: 'downtime-2', label: '2', color: '#11c1e8' },
        { id: 'downtime-10', label: '~10', color: '#c572ee' },
        { id: 'downtime-30', label: '> 30', color: '#f01625' },
    ],
    // Градиент для легенды "Количество самокатов на парковках"
    curScootersGradient: [
        { id: 'scooters-0', label: '0', color: 'white' },
        { id: 'scooters-1', label: '1', color: 'yellow' },
        { id: 'scooters-2', label: '2', color: '#11c1e8' },
        { id: 'scooters-10', label: '~10', color: '#c572ee' },
        { id: 'scooters-40', label: '~40', color: '#f6515c' },
        { id: 'scooters-50', label: '> 50', color: '#f01625' },
    ],
};

// Operation Areas Rotation - градиент для легенды "Операционные зоны" (Rotation)
// Цвета из CSS: rgb(9, 6, 96) → rgb(70, 3, 159) → rgb(115, 0, 168) → rgb(156, 23, 158) → rgb(190, 55, 133) → rgb(216, 87, 108) → rgb(253, 199, 38) → rgb(240, 249, 33)
export const OPERATION_AREAS_ROTATION_GRADIENT = [
    { id: 'rotation-1', label: '< 1', color: 'rgb(9, 6, 96)' },
    { id: 'rotation-2', label: '2', color: 'rgb(70, 3, 159)' },
    { id: 'rotation-3', label: '3', color: 'rgb(115, 0, 168)' },
    { id: 'rotation-4', label: '4', color: 'rgb(156, 23, 158)' },
    { id: 'rotation-5', label: '> 5', color: 'rgb(240, 249, 33)' },
];

// DA-12016: градиент для метрики "Старты" (trips_count)
// Диапазон по данным (period=7): p25≈27, p50≈200, p75≈467, p90≈782, p95≈1022, max≈6152
export const OA_TRIPS_GRADIENT = [
    { id: 'trips-1', label: '0',      color: 'rgb(9, 6, 96)' },
    { id: 'trips-2', label: '100',    color: 'rgb(28, 82, 178)' },
    { id: 'trips-3', label: '300',    color: 'rgb(30, 175, 200)' },
    { id: 'trips-4', label: '600',    color: 'rgb(120, 210, 130)' },
    { id: 'trips-5', label: '> 1000', color: 'rgb(240, 249, 33)' },
];

// DA-12016: градиент для метрики "Флот" (available_fleet_avg)
// Диапазон по данным (period=7): p25≈19, p50≈99, p75≈204, p90≈325, p95≈394, max≈1205
export const OA_FLEET_GRADIENT = [
    { id: 'fleet-1', label: '0',     color: 'rgb(9, 6, 96)' },
    { id: 'fleet-2', label: '50',    color: 'rgb(100, 30, 160)' },
    { id: 'fleet-3', label: '150',   color: 'rgb(200, 80, 80)' },
    { id: 'fleet-4', label: '300',   color: 'rgb(240, 160, 30)' },
    { id: 'fleet-5', label: '> 500', color: 'rgb(240, 249, 33)' },
];

// Operation Areas Rotation V2 - аналогичный градиент для "Операционные зоны v2"
export const OPERATION_AREAS_ROTATION_V2_GRADIENT = [
    { id: 'rotation-v2-1', label: '< 1', color: 'rgb(9, 6, 96)' },
    { id: 'rotation-v2-2', label: '2', color: 'rgb(70, 3, 159)' },
    { id: 'rotation-v2-3', label: '3', color: 'rgb(115, 0, 168)' },
    { id: 'rotation-v2-4', label: '4', color: 'rgb(156, 23, 158)' },
    { id: 'rotation-v2-5', label: '> 5', color: 'rgb(240, 249, 33)' },
];

// Operation Areas Rotation 2 - аналогичный градиент для третьей версии операционных зон
export const OPERATION_AREAS_ROTATION_2_GRADIENT = [
    { id: 'rotation-2-1', label: '< 1', color: 'rgb(9, 6, 96)' },
    { id: 'rotation-2-2', label: '2', color: 'rgb(70, 3, 159)' },
    { id: 'rotation-2-3', label: '3', color: 'rgb(115, 0, 168)' },
    { id: 'rotation-2-4', label: '4', color: 'rgb(156, 23, 158)' },
    { id: 'rotation-2-5', label: '> 5', color: 'rgb(240, 249, 33)' },
];

// Hex For Parkings - градиент для легенды "Гексагоны для парковок" (Индекс потенциала)
// Градиент от тёмного фиолетового к светло-зелёному (0.1 - 1.0)
export const HEX_PARKINGS_GRADIENT = [
    { id: 'hex-0.1', label: '0.1', color: '#0b0405' },
    { id: 'hex-0.2', label: '0.2', color: '#28192f' },
    { id: 'hex-0.3', label: '0.3', color: '#3b2f5f' },
    { id: 'hex-0.4', label: '0.4', color: '#3f498e' },
    { id: 'hex-0.5', label: '0.5', color: '#366b9f' },
    { id: 'hex-0.6', label: '0.6', color: '#348ba6' },
    { id: 'hex-0.7', label: '0.7', color: '#38abac' },
    { id: 'hex-0.8', label: '0.8', color: '#55caad' },
    { id: 'hex-0.9', label: '0.9', color: '#a3e0ba' },
    { id: 'hex-1.0', label: '1.0', color: '#def5e5' },
];

// Parking Alerts - алерты парковок
export const PARKING_ALERTS_STYLE = {
    statuses: [
        {
            value: 'PERMITTED',
            label: defineMessage({ message: 'Разрешено' }),
            color: 'green',
        },
        {
            value: 'LIMITED',
            label: defineMessage({ message: 'Алерт' }),
            color: 'orange',
        },
        {
            value: 'DENIED',
            label: defineMessage({ message: 'Запрет парковки' }),
            color: 'red',
        },
    ],
};

// Parking 1300 - статус парковок по 1300 постановлению
export const PARKING_1300_STYLE = {
    statuses: [
        {
            value: 'AGREED',
            label: defineMessage({ message: 'Согласовано' }),
            color: '#d9c22d',
        },
        {
            value: 'APPLICATION_SUBMITTED',
            label: defineMessage({ message: 'Подано заявление' }),
            color: '#452ef0',
        },
        {
            value: 'PAID_FOR_PERIOD',
            label: defineMessage({ message: 'Оплачено на период' }),
            color: '#33a02c',
        },
        {
            value: 'REFUSED',
            label: defineMessage({ message: 'Отказано' }),
            color: '#e31a1c',
        },
        {
            value: 'NOT_SUBMITTED',
            label: defineMessage({ message: 'Не подано' }),
            color: '3e3e3e',
        },
    ],
};

// Taps & Opens Days - количество дней для агрегации
export const TAPS_OPENS_DAYS_STYLE = {
    days: [
        {
            value: 7,
            label: defineMessage({ message: '7 дней' }),
            color: 'transparent',
        },
        {
            value: 30,
            label: defineMessage({ message: '30 дней' }),
            color: 'transparent',
        },
    ],
};

// Track Mode - режим отображения треков (абсолютное или нормализованное)
export const TRACK_MODE_STYLE = {
    modes: [
        {
            value: 'ABS',
            label: defineMessage({ message: 'Абсолютные' }),
            color: 'transparent',
        },
        {
            value: 'SHARE',
            label: defineMessage({ message: 'Нормализованные' }),
            color: 'transparent',
        },
    ],
};

// Track Days - периоды для агрегированных поездок (неделя, будни, выходные)
export const TRACK_DAYS_STYLE = {
    days: [
        {
            value: 'week',
            label: defineMessage({ message: 'Неделя' }),
            color: 'transparent',
        },
        {
            value: 'weekdays',
            label: defineMessage({ message: 'Будни' }),
            color: 'transparent',
        },
        {
            value: 'weekends',
            label: defineMessage({ message: 'Выходные' }),
            color: 'transparent',
        },
    ],
};

// Track Prefix - тип агрегированных поездок (все, самокаты, самокаты тандем, велосипеды)
export const TRACK_PREFIX_STYLE = {
    types: [
        {
            value: 'all',
            label: defineMessage({ message: 'Все поездки' }),
            color: 'transparent',
        },
        {
            value: 'scooters',
            label: defineMessage({ message: 'Самокаты' }),
            color: 'transparent',
        },
        {
            value: 'scooters_tandem',
            label: defineMessage({ message: 'Самокаты (тандем)' }),
            color: 'transparent',
        },
        {
            value: 'bikes',
            label: defineMessage({ message: 'Велосипеды' }),
            color: 'transparent',
        },
    ],
};

// Aggregated Track Days - дни недели для фильтра агрегированных поездок
export const AGGREGATED_TRACK_DAYS_STYLE = {
    days: [
        {
            value: 'week',
            label: defineMessage({ message: 'Неделя' }),
            color: 'hsla(301, 98%, 24%, 0)',
        },
        {
            value: 'weekdays',
            label: defineMessage({ message: 'Будни' }),
            color: 'hsla(301, 98%, 24%, 0)',
        },
        {
            value: 'weekends',
            label: defineMessage({ message: 'Выходные' }),
            color: 'hsla(301, 98%, 24%, 0)',
        },
    ],
};

// Aggregated Year - годы для фильтра агрегированных поездок
export const AGGREGATED_YEAR_STYLE = {
    years: [
        { value: '2019', label: defineMessage({ message: '2019' }), color: 'hsla(301, 98%, 24%, 0)' },
        { value: '2020', label: defineMessage({ message: '2020' }), color: 'hsla(301, 98%, 24%, 0)' },
        { value: '2021', label: defineMessage({ message: '2021' }), color: 'hsla(301, 98%, 24%, 0)' },
        { value: '2022', label: defineMessage({ message: '2022' }), color: 'hsla(301, 98%, 24%, 0)' },
        { value: '2023', label: defineMessage({ message: '2023' }), color: 'hsla(301, 98%, 24%, 0)' },
        { value: '2024', label: defineMessage({ message: '2024' }), color: 'hsla(301, 98%, 24%, 0)' },
        { value: '2025', label: defineMessage({ message: '2025' }), color: 'hsla(301, 98%, 24%, 0)' },
    ],
};

// AGG Years - годы для стартов/финишей агрегированных поездок (август, 2021-2025)
export const AGG_YEAR_AUGUST_STYLE = {
    years: [
        { value: '2021', label: defineMessage({ message: '2021' }), color: 'hsla(301, 98%, 24%, 0)' },
        { value: '2022', label: defineMessage({ message: '2022' }), color: 'hsla(301, 98%, 24%, 0)' },
        { value: '2023', label: defineMessage({ message: '2023' }), color: 'hsla(301, 98%, 24%, 0)' },
        { value: '2024', label: defineMessage({ message: '2024' }), color: 'hsla(301, 98%, 24%, 0)' },
        { value: '2025', label: defineMessage({ message: '2025' }), color: 'hsla(301, 98%, 24%, 0)' },
    ],
};

// Class Day Part - части дня для классифицированных парковок (ночь, утро, день, вечер, поздний вечер)
export const CLASS_DAY_PART_STYLE = {
    parts: [
        { value: 1, label: defineMessage({ message: 'Ночь (0-5)' }), color: 'hsla(301, 98%, 24%, 0)' },
        { value: 2, label: defineMessage({ message: 'Утро (6-11)' }), color: 'hsla(301, 98%, 24%, 0)' },
        { value: 3, label: defineMessage({ message: 'День (12-15)' }), color: 'hsla(301, 98%, 24%, 0)' },
        { value: 4, label: defineMessage({ message: 'Вечер (16-19)' }), color: 'hsla(301, 98%, 24%, 0)' },
        { value: 5, label: defineMessage({ message: 'Поздний вечер (20-23)' }), color: 'hsla(301, 98%, 24%, 0)' },
    ],
};

// Parking Permission - разрешение на парковку (разрешено/ограничено)
export const PARKING_PERMISSION_STYLE = {
    statuses: [
        { value: '1', label: defineMessage({ message: 'Permitted' }), color: 'green' },
        { value: '0', label: defineMessage({ message: 'Limited' }), color: 'orange' },
    ],
};

// OA Rotation Days - периоды для ротации операционных зон (1, 7, 14, 30 дней)
export const OA_ROTATION_DAYS_STYLE = {
    periods: [
        { value: 1, label: defineMessage({ message: '1 дн.' }), color: 'transparent' },
        { value: 7, label: defineMessage({ message: '7 дн.' }), color: 'transparent' },
        { value: 14, label: defineMessage({ message: '14 дн.' }), color: 'transparent' },
        { value: 30, label: defineMessage({ message: '30 дн.' }), color: 'transparent' },
    ],
};

// Parking Lot Status - статусы добавления парковок (деактивировано, вручную добавлено, системно добавлено, проверено, предварительно проверено)
export const PARKING_LOT_STATUS_STYLE = {
    statuses: [
        { value: 'DEACTIVATED', label: defineMessage({ message: 'Деактивировано' }), color: '#3d5cf6' },
        { value: 'MANUAL_ADDED', label: defineMessage({ message: 'Вручную добавлено' }), color: '#ff03bc' },
        { value: 'SYSTEM_ADDED', label: defineMessage({ message: 'Системно добавлено' }), color: '#F7FE2E' },
        { value: 'VERIFIED', label: defineMessage({ message: 'Проверено' }), color: '#0dd75b' },
        { value: 'PRE-VERIFIED', label: defineMessage({ message: 'Предварительно проверено' }), color: 'red' },
    ],
};

// Competitors Days - периоды для анализа конкурентов (1, 7, 30 дней)
export const COMPETITORS_DAYS_STYLE = {
    periods: [
        { value: 1, label: defineMessage({ message: '1 дн.' }), color: 'transparent' },
        { value: 7, label: defineMessage({ message: '7 дн.' }), color: 'transparent' },
        { value: 30, label: defineMessage({ message: '30 дн.' }), color: 'transparent' },
    ],
};

// Functional Areas Yandex Urent - зоны конкурентов (Yandex, Urent, Jet, Lime)
export const FUNCTIONAL_AREAS_YANDEX_URENT_STYLE = {
    zones: [
        { value: 'Yandex', label: defineMessage({ message: 'Желтые' }), color: '#fedf3e' },
        { value: 'Urent', label: defineMessage({ message: 'Фиолетовые' }), color: '#804aff' },
        { value: 'Jet', label: defineMessage({ message: 'Синие' }), color: '#2f7fe2' },
        { value: 'Lime', label: defineMessage({ message: 'Зеленые' }), color: '#00dd01' },
        { value: 'Voi', label: defineMessage({ message: 'Коралловые' }), color: '#f26960' },
        { value: 'Eleven', label: defineMessage({ message: 'Жёлтые (Eleven)' }), color: '#ffbf00' },
    ],
};

// Lisboa Zones - зоны ограничений в Лиссабоне (No parking, Forbidden, Mandatory parking)
export const LISBOA_ZONES_STYLE = {
    zones: [
        { value: 'No parking', label: defineMessage({ message: 'Зона запрета парковки' }), color: 'orange' },
        { value: 'Forbidden', label: defineMessage({ message: 'Зона запрета катания' }), color: 'red' },
        { value: 'Mandatory parking', label: defineMessage({ message: 'Обязательная парковка' }), color: 'yellow' },
    ],
};

// AGG User Taps 7D - легенда тапов за 7 дней (6 диапазонов)
export const AGG_USER_TAPS_7D_STYLE = {
    ranges: [
        { value: '0', label: '< 50', color: 'hsla(242, 89%, 28%, 0.5)' },
        { value: '1', label: defineMessage({ message: 'от {0} до {1}', values: { 0: 50, 1: 100 } }), color: '#792282' },
        { value: '2', label: defineMessage({ message: 'от {0} до {1}', values: { 0: 100, 1: 300 } }), color: '#c23b75' },
        { value: '3', label: defineMessage({ message: 'от {0} до {1}', values: { 0: 300, 1: 500 } }), color: '#f4695c' },
        { value: '4', label: defineMessage({ message: 'от {0} до {1}', values: { 0: 500, 1: 700 } }), color: '#feb47b' },
        { value: '5', label: '> 700', color: '#fcfdbf' },
    ],
};

// AGG User Taps 30D - легенда тапов за 30 дней (6 диапазонов, same as 7D)
export const AGG_USER_TAPS_30D_STYLE = {
    ranges: [
        { value: '0', label: '< 50', color: 'hsla(242, 89%, 28%, 0.5)' },
        { value: '1', label: defineMessage({ message: 'от {0} до {1}', values: { 0: 50, 1: 100 } }), color: '#792282' },
        { value: '2', label: defineMessage({ message: 'от {0} до {1}', values: { 0: 100, 1: 300 } }), color: '#c23b75' },
        { value: '3', label: defineMessage({ message: 'от {0} до {1}', values: { 0: 300, 1: 500 } }), color: '#f4695c' },
        { value: '4', label: defineMessage({ message: 'от {0} до {1}', values: { 0: 500, 1: 700 } }), color: '#feb47b' },
        { value: '5', label: '> 700', color: '#fcfdbf' },
    ],
};

// AGG User Opens 7D - легенда открытий за 7 дней (6 диапазонов)
export const AGG_USER_OPENS_7D_STYLE = {
    ranges: [
        { value: '0', label: '< 200', color: 'hsla(242, 89%, 28%, 0.5)' },
        { value: '1', label: defineMessage({ message: 'от {0} до {1}', values: { 0: 200, 1: 500 } }), color: '#792282' },
        { value: '2', label: defineMessage({ message: 'от {0} до {1}', values: { 0: 500, 1: 800 } }), color: '#c23b75' },
        { value: '3', label: defineMessage({ message: 'от {0} до {1}', values: { 0: 800, 1: 1200 } }), color: '#f4695c' },
        { value: '4', label: defineMessage({ message: 'от {0} до {1}', values: { 0: 1200, 1: 2000 } }), color: '#feb47b' },
        { value: '5', label: '> 2000', color: '#fcfdbf' },
    ],
};

// AGG User Opens 30D - легенда открытий за 30 дней (6 диапазонов)
export const AGG_USER_OPENS_30D_STYLE = {
    ranges: [
        { value: '0', label: '< 200', color: 'hsla(242, 89%, 28%, 0.5)' },
        { value: '1', label: defineMessage({ message: 'от {0} до {1}', values: { 0: 200, 1: 500 } }), color: '#792282' },
        { value: '2', label: defineMessage({ message: 'от {0} до {1}', values: { 0: 500, 1: 800 } }), color: '#c23b75' },
        { value: '3', label: defineMessage({ message: 'от {0} до {1}', values: { 0: 800, 1: 1200 } }), color: '#f4695c' },
        { value: '4', label: defineMessage({ message: 'от {0} до {1}', values: { 0: 1200, 1: 2000 } }), color: '#feb47b' },
        { value: '5', label: '> 2000', color: '#fcfdbf' },
    ],
};

// Isochrones With Scooters - тип изохрон (все / только с самокатами)
export const ISOCHRONES_WITH_SCOOTERS_STYLE = {
    types: [
        { value: '0', label: defineMessage({ message: 'Все изохроны' }), color: '#369ac2' },
        { value: '1', label: defineMessage({ message: 'Изохроны с самокатами' }), color: 'red' },
    ],
};

// Analytic Areas - аналитические зоны (Транспортный каркас, Бизнес)
export const ANALYTIC_AREAS_STYLE = {
    zones: [
        { value: 0, label: defineMessage({ message: 'Транспортный каркас' }), color: 'hsla(301, 98%, 24%, 0)' },
        { value: 1, label: defineMessage({ message: 'Бизнес' }), color: 'hsla(301, 98%, 24%, 0)' },
    ],
};

// Service Centers - типы сервисных центров (ACTIVE, СЦ, СЗ, Шкаф, PROSPECTIVE, ПСЦ-АДМИН, ПСЗ-АДМИН, ПШкаф)
export const SERVICE_CENTERS_FILTER_STYLE = {
    centers: [
        { value: 'ACTIVE', label: defineMessage({ message: 'Фактическая инфраструктура' }), color: '#00c04b' },
        { value: 'СЦ', label: defineMessage({ message: 'Сервисный центр' }), color: '#89CFF0' },
        { value: 'СЗ', label: defineMessage({ message: 'Станция зарядки' }), color: 'red' },
        { value: 'Шкаф', label: defineMessage({ message: 'Шкаф' }), color: 'blue' },
        { value: 'PROSPECTIVE', label: defineMessage({ message: 'Планируемая инфраструктура' }), color: 'orange' },
        { value: 'ПСЦ-АДМИН', label: defineMessage({ message: 'Перспективный сервисный центр' }), color: 'purple' },
        { value: 'ПСЗ-АДМИН', label: defineMessage({ message: 'Перспективная станция зарядки' }), color: 'pink' },
        { value: 'ПШкаф', label: defineMessage({ message: 'Перспективный шкаф' }), color: '#7B3F00' },
    ],
};

// SB With Errors - типы ошибок для сервисных самокатов (21, 50)
export const SB_WITH_ERRORS_STYLE = {
    errors: [
        { value: '21', label: defineMessage({ message: '21 (Контакт с АКБ)' }), color: '#CC2828' },
        { value: '50', label: defineMessage({ message: '50 (Тип АКБ)' }), color: '#AA0CB0' },
    ],
};

// Operation Areas - статусы операционных зон (active, inactive)
export const OPERATION_AREAS_STATUS_STYLE = {
    statuses: [
        { value: 'active', label: defineMessage({ message: 'Активные' }), color: 'transparent' },
        { value: 'inactive', label: defineMessage({ message: 'Неактивные' }), color: 'transparent' },
    ],
};

// Competitors Prefix Mode - режимы отображения для конкурентов (starts/ABS, ms/MARKET_SHARE, leadership/LEADER)
export const COMPETITORS_PREFIX_MODE_STYLE = {
    modes: [
        { value: 'starts', label: 'Абсолютные', color: 'transparent' },
        { value: 'ms', label: 'MARKET SHARE', color: 'transparent' },
        { value: 'leadership', label: 'LEADER', color: 'transparent' },
    ],
};

// Competitors - список конкурентов (whoosh, yandex, urent, jet, eleven, lime, lite)
export const COMPETITORS_FILTER_STYLE = {
    competitors: [
        { value: 'whoosh', label: 'Whoosh', color: 'transparent' },
        { value: 'yandex', label: 'Yandex', color: 'transparent' },
        { value: 'urent', label: 'Urent', color: 'transparent' },
        { value: 'jet', label: 'Jet', color: 'transparent' },
        { value: 'eleven', label: 'Eleven', color: 'transparent' },
        { value: 'lime', label: 'Lime', color: 'transparent' },
        { value: 'lite', label: 'Lite', color: 'transparent' },
        { value: 'voi', label: 'Voi', color: 'transparent' },
    ],
};

// Alerts Types - типы алертов (DENIED, LIMITED, PERMITTED)
export const ALERTS_TYPES_STYLE = {
    types: [
        { value: 1, label: 'DENIED', color: 'transparent' },
        { value: 2, label: 'LIMITED', color: 'transparent' },
        { value: 3, label: 'PERMITTED', color: 'transparent' },
    ],
};

// Alerts Device Types - типы устройств для алертов (DEVICE, PHONE)
export const ALERTS_DEVICE_TYPES_STYLE = {
    devices: [
        { value: 1, label: 'DEVICE', color: 'transparent' },
        { value: 2, label: 'PHONE', color: 'transparent' },
    ],
};

// Parkings Alerts Error Types - типы ошибок для алертов парковок (11 типов + All cases)
export const PARKINGS_ALERTS_ERROR_TYPES_STYLE = {
    errors: [
        { value: 1,  label: 'DEVICE_LIMITEXT_ONLINE__PHONE_LIMITEXT', color: '#E34234' },
        { value: 2,  label: 'DEVICE_DENIED',                          color: '#E34234' },
        { value: 3,  label: 'DEVICE_OFFLINE__PHONE_LIMITEXT',         color: '#6495ED' },
        { value: 4,  label: 'DENIED_BY_FORBIDDEN_AREA',               color: '#E34234' },
        { value: 5,  label: 'DENIED_BY_NO_LOCKING_AREA',              color: '#E34234' },
        { value: 7,  label: 'SMS_FORBIDDEN_PARKING',                  color: '#E34234' },
        { value: 9,  label: 'DEVICE_DENIED__PHONE_NOGEO',             color: '#E34234' },
        { value: 10, label: 'RESTRICTED_FOR_TYPE_AREA',               color: '#E34234' },
        { value: 11, label: 'DEVICE_NOGEO__PHONE_LIMITEXT',           color: '#6495ED' },
        { value: 12, label: 'All cases',                              color: 'White' },
    ],
};

// Parkings Alerts Device - типы устройств для алертов парковок (PHONE, DEVICE)
export const PARKINGS_ALERTS_DEVICE_STYLE = {
    devices: [
        { value: 'PHONE', label: defineMessage({ message: 'PHONE' }), color: '#6495ED' },
        { value: 'DEVICE', label: defineMessage({ message: 'DEVICE' }), color: '#E34234' },
    ],
};

// Parkings Alerts Period - периоды для алертов парковок (1, 3 дня)
export const PARKINGS_ALERTS_PERIOD_STYLE = {
    periods: [
        { value: 1, label: defineMessage({ message: '1 дн.' }), color: 'transparent' },
        { value: 3, label: defineMessage({ message: '3 дн.' }), color: 'transparent' },
    ],
};

// Taps/Opens Mode - режимы отображения (Абсолютные / Нормализованные)
export const TAPS_OPENS_MODE_STYLE = {
    modes: [
        { value: 'ABS', label: defineMessage({ message: 'Абсолютные' }), color: 'transparent' },
        { value: 'SHARE', label: defineMessage({ message: 'Нормализованные' }), color: 'transparent' },
    ],
};

// Start/Finish Track Days - периоды для старт/финиш треков (1, 7, 30 дней)
export const START_FINISH_TRACK_DAYS_STYLE = {
    periods: [
        { value: 1, label: defineMessage({ message: '1 дн.' }), color: 'transparent' },
        { value: 7, label: defineMessage({ message: '7 дн.' }), color: 'transparent' },
        { value: 30, label: defineMessage({ message: '30 дн.' }), color: 'transparent' },
    ],
};

// Taps/Opens Periods - периоды для тапов и открытий (7, 30 дней)
export const TAPS_OPENS_PERIODS_STYLE = {
    periods: [
        { value: 7, label: defineMessage({ message: '7 дней' }), color: 'transparent' },
        { value: 30, label: defineMessage({ message: '30 дней' }), color: 'transparent' },
    ],
};

// Whoosh Hotspot (Stationary Parking) - стационарная парковка
export const WHOOSH_HOTSPOT_STYLE = {
    items: {
        items: [
            {
                id: 'hotspot-1',
                label: defineMessage({ message: 'Стационарная парковка' }),
                color: '#0dd75b',
            },
        ],
    },
};

// Whoosh Parking Types — различие по обводке (circle-stroke)
export const WHOOSH_PARKING_TYPE_STYLE = {
    types: [
        {
            id: 'parking-type-physical',
            label: defineMessage({ message: 'Физическая' }),
            color: '#0dd75b',
            strokeColor: 'grey',
            strokeWidth: 2.5,
        },
        {
            id: 'parking-type-virtual',
            label: defineMessage({ message: 'Виртуальная' }),
            color: '#0dd75b',
            strokeColor: 'black',
            strokeWidth: 1.5,
        },
    ],
};

// Starts by Hour - размеры кругов для концентрической легенды
export const STARTS_BY_HOUR_SIZE_STYLE = {
    quantities: [
        { id: 'starts-qty-1', label: defineMessage({ message: '1 старт' }), value: 1, radius: 6 },
        { id: 'starts-qty-2', label: defineMessage({ message: '10 стартов' }), value: 10, radius: 14 },
        { id: 'starts-qty-3', label: defineMessage({ message: '20 стартов' }), value: 20, radius: 22 },
    ],
};

// Starts by Hour - цвета для типов стартов
export const STARTS_BY_HOUR_COLORS_STYLE = {
    types: [
        { id: 'starts-type-from-parking', label: defineMessage({ message: 'Start from parking' }), color: '#ff5252' },
        { id: 'starts-type-no-parking', label: defineMessage({ message: 'No nearby parking found' }), color: '#3388ff' },
    ],
};

// Форкаст ребаланса (Rebalance Forecast)
// cluster=0 → профицит (забираем), cluster=4/5 → дефицит (привозим)
export const REBALANCE_FORECAST_STYLE = {
    categories: [
        { id: 'rebal-forecast-surplus',  label: defineMessage({ message: 'Профицит (забираем)' }), color: '#f5c800', cluster: 0 },
        { id: 'rebal-forecast-deficit',  label: defineMessage({ message: 'Дефицит (привозим)' }),  color: '#e8534a', cluster: '4, 5' },
    ],
};

// Размерная градуировка для слоя "Форкаст ребаланса" (по полю forecast)
export const REBALANCE_FORECAST_SIZE_STYLE = {
    forecast: {
        items: [
            { id: 'forecast-size-1', label: defineMessage({ message: '~5' }),   value: 5,   radius: 5  },
            { id: 'forecast-size-2', label: defineMessage({ message: '~60' }),  value: 60,  radius: 11 },
            { id: 'forecast-size-3', label: defineMessage({ message: '≥ 250' }), value: 250, radius: 18 },
        ],
    },
};

// Таба ребаланса (delta = forecast − cnt_scooters)
// delta > 0 → дефицит (привозим), красный; delta < 0 → профицит (забираем), жёлтый
export const REBALANCE_DELTA_STYLE = {
    categories: [
        { id: 'rebal-delta-deficit',  label: defineMessage({ message: 'Дефицит (привозим)' }),  color: '#e8534a' },
        { id: 'rebal-delta-surplus',  label: defineMessage({ message: 'Профицит (забираем)' }), color: '#f5c800' },
    ],
    delta: {
        items: [
            { id: 'delta-size-1', label: defineMessage({ message: '~5' }),   value: 5,   radius: 4  },
            { id: 'delta-size-2', label: defineMessage({ message: '~10' }),  value: 10,  radius: 10 },
            { id: 'delta-size-3', label: defineMessage({ message: '≥ 50' }), value: 50,  radius: 18 },
        ],
    },
};

// АРХИТЕКТУРА LAYERSTYLES.JS:
// Этот файл содержит конфигурации для всех легенд в FilterSidebar.
// Используйте эти переменные вместо hardcoded значений в компонентах.
//
// СТРУКТУРЫ:
// 1. LAYER_NAME_STYLE для GradientLegend: { propertyName: [{ id, label, color }] }
// 2. LAYER_NAME_STYLE для ColoredItemsLegend: { items: [{ id, label, color }] }
// 3. LAYER_NAME_MAPBOX_PAINT для Map.jsx: MapBox expression с интерполяциями
// 4. FILTER_STYLE для фильтров: { style, styleKey, filterKey }
//
// ПРИМЕРЫ:
// - GradientLegend: import { DOWNTIME_SCOOTERS_STYLE } from layerStyles
//   <GradientLegend items={DOWNTIME_SCOOTERS_STYLE.downtime48hGradient} renderConfig={config} />
// - ColoredItemsLegend: import { PARKING_ALERTS_STYLE } from layerStyles
//   <ColoredItemsLegend items={PARKING_ALERTS_STYLE.statuses} renderConfig={config} />
//
// ДОБАВЛЕНИЕ НОВЫХ СТИЛЕЙ:
// 1. Создай export const LAYER_NAME_STYLE с нужной структурой
// 2. Импортируй в FilterSidebar компонент (src/components/FilterSidebar/...)
// 3. Используй вместо hardcoded значений
// 4. Все labels должны быть обёрнуты в defineMessage() для i18n

// ─────────────────────────────────────────────────────────────
// Фактический ребаланс (Fact Rebalance)
// Цвета: только увезли → красный, только привезли → зелёный, оба → синий
// Периоды суток: 8 временных слотов (Ночь_1…Вечер_2), индексируются 0–7
// ─────────────────────────────────────────────────────────────
export const FACT_REBALANCE_COLORS_STYLE = {
    actions: [
        { id: 'fact-reb-took',   label: defineMessage({ message: 'Только увезли' }),        color: '#FF5733' },
        { id: 'fact-reb-brought', label: defineMessage({ message: 'Только привезли' }),      color: '#33FF57' },
        { id: 'fact-reb-both',   label: defineMessage({ message: 'Увезли и привезли' }),     color: '#3380FF' },
    ],
};

export const FACT_REBALANCE_SIZE_STYLE = {
    quantities: [
        { id: 'fact-reb-sz-1', label: defineMessage({ message: '1' }),   value: 1,  radius: 4  },
        { id: 'fact-reb-sz-2', label: defineMessage({ message: '4' }),   value: 4,  radius: 8  },
        { id: 'fact-reb-sz-3', label: defineMessage({ message: '16' }),  value: 16, radius: 16 },
    ],
};

// Агрегированные треки за последние 7 дней (H3 zoom 6-12)
// Базовые значения для zoom=10, масштабируются как 7^(zoom-10)
export const ADMIN_AGG_TRACKS_V2_STYLE = {
    tracks: [
        { id: 'agg-tracks-v2-1', label: defineMessage({ message: '<0.3K' }), color: '#0d0887' },
        { id: 'agg-tracks-v2-2', label: defineMessage({ message: '1K' }),    color: '#6b00a8' },
        { id: 'agg-tracks-v2-3', label: defineMessage({ message: '4K' }),    color: '#e16461' },
        { id: 'agg-tracks-v2-4', label: defineMessage({ message: '8K' }),    color: '#fca736' },
        { id: 'agg-tracks-v2-5', label: defineMessage({ message: '>25K' }),  color: '#f0f921' },
    ],
};

// DA-15259: доля продуктовой когорты в гексагоне (cohort_cnt / all_cnt).
// Шкала фиксированная 0–100%, от зума не зависит — сравниваем структуру, а не объём.
export const AGG_TRACKS_COHORT_SHARE_STYLE = {
    share: [
        { id: 'agg-tracks-cohort-share-1', label: defineMessage({ message: '<5%' }),  color: '#0d0887' },
        { id: 'agg-tracks-cohort-share-2', label: defineMessage({ message: '15%' }),  color: '#6b00a8' },
        { id: 'agg-tracks-cohort-share-3', label: defineMessage({ message: '30%' }),  color: '#e16461' },
        { id: 'agg-tracks-cohort-share-4', label: defineMessage({ message: '50%' }),  color: '#fca736' },
        { id: 'agg-tracks-cohort-share-5', label: defineMessage({ message: '>70%' }), color: '#f0f921' },
    ],
};

// DA-15415: доля бакета когорты пользователей в гексагоне (<bucket>_cnt / all_cnt).
// Шкала фиксированная 0–100%, от зума не зависит — сравниваем структуру, а не объём.
export const USER_COHORTS_SHARE_STYLE = {
    share: [
        { id: 'user-cohorts-share-1', label: defineMessage({ message: '<5%' }),  color: '#0d0887' },
        { id: 'user-cohorts-share-2', label: defineMessage({ message: '15%' }),  color: '#6b00a8' },
        { id: 'user-cohorts-share-3', label: defineMessage({ message: '30%' }),  color: '#e16461' },
        { id: 'user-cohorts-share-4', label: defineMessage({ message: '50%' }),  color: '#fca736' },
        { id: 'user-cohorts-share-5', label: defineMessage({ message: '>70%' }), color: '#f0f921' },
    ],
};

// Метки периодов суток для слайдера фильтра
// Индекс совпадает с полем period_index в MVT-функции
export const FACT_REBALANCE_PERIODS = [
    { index: 0, key: 'Ночь_1',   label: defineMessage({ message: 'Ночь (0–3)'   }) },
    { index: 1, key: 'Ночь_2',   label: defineMessage({ message: 'Ночь (3–6)'   }) },
    { index: 2, key: 'Утро_1',   label: defineMessage({ message: 'Утро (6–9)'   }) },
    { index: 3, key: 'Утро_2',   label: defineMessage({ message: 'Утро (9–12)'  }) },
    { index: 4, key: 'День_1',   label: defineMessage({ message: 'День (12–15)' }) },
    { index: 5, key: 'День_2',   label: defineMessage({ message: 'День (15–18)' }) },
    { index: 6, key: 'Вечер_1',  label: defineMessage({ message: 'Вечер (18–21)' }) },
    { index: 7, key: 'Вечер_2',  label: defineMessage({ message: 'Вечер (21–24)' }) },
];

// DA-12966: Потерянные самокаты (LOST / DELETED)
export const LOST_DEVICES_STYLE = {
    categories: [
        { id: 'lost',    value: 'LOST',    label: defineMessage({ message: 'LOST — потерян'              }), color: '#eab308' },
        { id: 'deleted', value: 'DELETED', label: defineMessage({ message: 'DELETED — утерян безвозвратно' }), color: '#ef4444' },
    ],
};

// DA-12966: Лосты на гексагонах — viridis градиент по cnt_total
// DA-12966: plasma-палитра (как у «Агрегированных стартов»), отсечки подобраны по перцентилям zoom=10:
// p50=1, p75=1, p90=2, p95=3, p99=10, max=995 — 18 589 гексов
// DA-16000: слой «Wifi точки».
// Цвет точек — по числу наблюдений, та же plasma-палитра, что у агрегированных треков.
// Пороги по перцентилям базы: p50 = 8, p90 = 38, p99 = 174 наблюдений.
export const WIFI_AP_OBS_STYLE = {
    obs: {
        items: [
            { id: 'wifi-ap-obs-1', label: defineMessage({ message: '<2' }),   color: '#0d0887' },
            { id: 'wifi-ap-obs-2', label: defineMessage({ message: '8' }),    color: '#6b00a8' },
            { id: 'wifi-ap-obs-3', label: defineMessage({ message: '25' }),   color: '#e16461' },
            { id: 'wifi-ap-obs-4', label: defineMessage({ message: '80' }),   color: '#fca736' },
            { id: 'wifi-ap-obs-5', label: defineMessage({ message: '>300' }), color: '#f0f921' },
        ],
    },
};

// DA-16000: плотность покрытия — точек доступа в H3-ячейке.
// Базовые значения для zoom = 12, самого мелкого уровня (перцентили на нём:
// p50 = 1, p75 = 2, p90 = 4, p99 = 13). База именно 12, а не 10: легенда показывает
// пороги в пересчёте на текущий уровень H3, и при базе 10 на двенадцатом зуме
// получались бы доли точки («0.1 точки доступа» вместо «1»).
// На уровнях крупнее шкала домножается на 4^(12−zoom). Множитель 4, а не «геометрические»
// 7 дочерних ячеек: по факту число точек в ячейке растёт примерно вчетверо на уровень
// (p90 по уровням 12→7: 4, 15, 62, 302, 1654, 8612). С семёркой шкала на крупных уровнях
// уезжает выше реальных значений и карта выцветает в синее.
export const WIFI_AP_H3_STYLE = {
    density: [
        { id: 'wifi-ap-h3-1', label: defineMessage({ message: '1' }),   color: '#0d0887' },
        { id: 'wifi-ap-h3-2', label: defineMessage({ message: '2' }),   color: '#6b00a8' },
        { id: 'wifi-ap-h3-3', label: defineMessage({ message: '4' }),   color: '#e16461' },
        { id: 'wifi-ap-h3-4', label: defineMessage({ message: '8' }),   color: '#fca736' },
        { id: 'wifi-ap-h3-5', label: defineMessage({ message: '>16' }), color: '#f0f921' },
    ],
};

export const LOST_DEVICES_H3_STYLE = {
    total: {
        items: [
            { id: 'lost-h3-1', label: defineMessage({ message: '<2' }),  color: '#0d0887' },
            { id: 'lost-h3-2', label: defineMessage({ message: '2' }),   color: '#6b00a8' },
            { id: 'lost-h3-3', label: defineMessage({ message: '5' }),   color: '#e16461' },
            { id: 'lost-h3-4', label: defineMessage({ message: '15' }),  color: '#fca736' },
            { id: 'lost-h3-5', label: defineMessage({ message: '>50' }), color: '#f0f921' },
        ],
    },
};

// DA-14308: Выбросы GPS-треков по H3-гексагонам
// Медиана дистанции выброса (в метрах). Bins подобраны по перцентилям на zoom=12.
// plasma-палитра: p25≈25м, p50≈60м, p75≈140м, p90≈320м, p95≈700м

export const ROUTE_ANOMALIES_H3_STYLE = {
    // Стопы по перцентилям zoom=12: p50=316, p75=414, p90=545, p95=679
    // Значения не зависят от зума (scalingFactor=1)
    medianDist: [
        { id: 'anomaly-h3-1', label: defineMessage({ message: '<300м' }),    color: '#0d0887' },
        { id: 'anomaly-h3-2', label: defineMessage({ message: '300–420м' }), color: '#6b00a8' },
        { id: 'anomaly-h3-3', label: defineMessage({ message: '420–560м' }), color: '#e16461' },
        { id: 'anomaly-h3-4', label: defineMessage({ message: '560–700м' }), color: '#fca736' },
        { id: 'anomaly-h3-5', label: defineMessage({ message: '>700м' }),    color: '#f0f921' },
    ],
    // Логарифмическая шкала: p90 zoom12=3, p95=6, p99=18, max=1401
    // Ступени подобраны по scaled values (cnt * 7^(zoom-10)):
    // 10≈p50@z12, 50≈p75@z12, 150≈p90@z12, 500≈p95@z12, 2000≈p99@z12
    cnt: [
        { id: 'anomaly-h3-cnt-1', label: defineMessage({ message: '1–2' }),    color: '#0d0887' },
        { id: 'anomaly-h3-cnt-2', label: defineMessage({ message: '3–10' }),   color: '#5302a3' },
        { id: 'anomaly-h3-cnt-3', label: defineMessage({ message: '11–30' }),  color: '#8b0aa5' },
        { id: 'anomaly-h3-cnt-4', label: defineMessage({ message: '31–100' }), color: '#cb4679' },
        { id: 'anomaly-h3-cnt-5', label: defineMessage({ message: '101–400' }), color: '#f0703a' },
        { id: 'anomaly-h3-cnt-6', label: defineMessage({ message: '401–1200' }), color: '#fca636' },
        { id: 'anomaly-h3-cnt-7', label: defineMessage({ message: '>1200' }),  color: '#f0f921' },
    ],
};

// DA-14919: WalkScore по H3 (тестовый DEV-слой). Шкала 0–100 одна на все метрики
// (15 категорий + 5 групп + общий) — переключение метрики меняет лишь крашенный атрибут.
export const WALKSCORE_STYLE = {
    score: {
        items: [
            { id: 'walkscore-1', label: defineMessage({ message: '0' }),   color: '#440154' },
            { id: 'walkscore-2', label: defineMessage({ message: '25' }),  color: '#3b528b' },
            { id: 'walkscore-3', label: defineMessage({ message: '50' }),  color: '#21918c' },
            { id: 'walkscore-4', label: defineMessage({ message: '75' }),  color: '#5ec962' },
            { id: 'walkscore-5', label: defineMessage({ message: '100' }), color: '#fde725' },
        ],
    },
};
